import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { projectApi, openRouterApi, mcpApi, templateApi, expertFeedbackApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import {
  ArrowLeft,
  Bookmark,
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Share2,
  Download,
  Copy,
  User,
  FileText,
  AlertCircle,
  MessageSquare,
  RefreshCw,
  DollarSign,
  Hash,
  Timer,
  Sparkles,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  History,
  Send,
  Search,
  Plus,
  Minus,
  Brain,
  Calculator,
  Pencil,
  Check,
  X,
  Settings,
  Server,
  Plug,
  Paperclip,
  File,
  Image,
  Trash2,
  Info,
  GripVertical,
  Globe,
  Lightbulb,
  Cpu,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const StatusBadge = ({ status, language = 'it' }) => {
  const config = {
    pending: { color: 'bg-muted text-muted-foreground', icon: Clock, label: { it: 'In attesa', en: 'Pending' } },
    running: { color: 'bg-warning/20 text-warning', icon: Loader2, animate: true, label: { it: 'In corso', en: 'Running' } },
    completed: { color: 'bg-success/20 text-success', icon: CheckCircle2, label: { it: 'Completato', en: 'Completed' } },
    failed: { color: 'bg-destructive/20 text-destructive', icon: XCircle, label: { it: 'Fallito', en: 'Failed' } },
    clarification: { color: 'bg-blue-500/20 text-blue-500', icon: MessageCircle, animate: true, label: { it: 'Chiarimenti', en: 'Clarification' } },
    waiting_for_input: { color: 'bg-info/20 text-info', icon: MessageCircle, label: { it: 'Domande', en: 'Questions' } },
  };

  const statusConfig = config[status] || config.pending;
  const { color, icon: Icon, animate, label } = statusConfig;

  return (
    <Badge className={`${color} gap-1 font-code text-xs uppercase tracking-wider`}>
      <Icon className={`w-3 h-3 ${animate ? 'animate-spin' : ''}`} />
      {label?.[language] || status}
    </Badge>
  );
};

const ConfidenceBadge = ({ score }) => {
  if (!score) return null;
  const color = score >= 8 ? 'text-success' : score >= 5 ? 'text-warning' : 'text-destructive';
  return (
    <span className={`text-xs font-code ${color}`}>
      {score}/10
    </span>
  );
};

// Progress Steps Component - Display only, not clickable
const ProgressSteps = ({ phase, language }) => {
  const steps = [
    { key: 'analysis', phase: 'analyzing', label: language === 'it' ? 'Analisi' : 'Analysis' },
    { key: 'clarification', phase: 'clarification', label: language === 'it' ? 'Chiarimenti' : 'Clarification' },
    { key: 'configuration', phase: 'configuration', label: language === 'it' ? 'Configurazione' : 'Configuration' },
    { key: 'executing', phase: 'executing', label: language === 'it' ? 'Esecuzione' : 'Executing' },
    { key: 'completed', phase: 'completed', label: language === 'it' ? 'Completato' : 'Completed' },
  ];

  const phaseMap = {
    'analyzing': 'analysis',
    'clarification': 'clarification',
    'questions': 'clarification',
    'configuration': 'configuration',
    'ready': 'executing',
    'executing': 'executing',
    'recalculating': 'executing',
    'paused': 'executing',
    'completed': 'completed',
    'error': 'completed',
  };

  const currentPhase = phaseMap[phase] || 'analysis';
  const currentIndex = steps.findIndex(s => s.key === currentPhase);
  const isFullyCompleted = phase === 'completed';

  return (
    <div className="flex items-center gap-2 mb-6">
      {steps.map((step, index) => {
        // When fully completed, all steps should show as completed
        const isCompleted = isFullyCompleted || index < currentIndex;
        const isActive = !isFullyCompleted && index === currentIndex;
        const isPending = !isFullyCompleted && index > currentIndex;

        return (
          <React.Fragment key={step.key}>
            <div className="flex items-center gap-2">
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all
                ${isActive ? 'bg-primary text-primary-foreground animate-pulse' : ''}
                ${isCompleted ? 'bg-success text-success-foreground' : ''}
                ${isPending ? 'bg-muted text-muted-foreground' : ''}
              `}>
                {isCompleted ? <CheckCircle2 className="w-4 h-4" /> : index + 1}
              </div>
              <span className={`text-xs font-medium hidden sm:inline ${isActive ? 'text-primary' : isPending ? 'text-muted-foreground' : 'text-foreground'}`}>
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className={`flex-1 h-0.5 max-w-8 ${isCompleted ? 'bg-success' : 'bg-muted'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Model Selector with Search
const ModelSelector = ({ value, onChange, models, openSourceOnly, language }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Filter models
  const filteredModels = models.filter(m => {
    if (openSourceOnly) {
      const isOS = m.is_free ||
        m.id.includes('llama') ||
        m.id.includes('mixtral') ||
        m.id.includes('qwen') ||
        m.id.includes('gemma') ||
        m.id.includes('phi') ||
        m.id.includes('mistral');
      if (!isOS) return false;
    }
    if (search) {
      const s = search.toLowerCase();
      return (m.name || '').toLowerCase().includes(s) || (m.id || '').toLowerCase().includes(s);
    }
    return true;
  }).slice(0, 100);

  const selectedModel = models.find(m => m.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-background hover:bg-background"
        >
          <span className="truncate">
            {selectedModel?.name || value || (language === 'it' ? 'Seleziona modello...' : 'Select model...')}
          </span>
          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder={language === 'it' ? 'Cerca modello...' : 'Search model...'}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{language === 'it' ? 'Nessun modello trovato.' : 'No model found.'}</CommandEmpty>
            <CommandGroup>
              {filteredModels.map((model) => (
                <CommandItem
                  key={model.id}
                  value={model.id}
                  onSelect={() => {
                    onChange(model.id);
                    setOpen(false);
                    setSearch('');
                  }}
                  className="cursor-pointer"
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex flex-col">
                      <span className="font-medium">{model.name}</span>
                      <span className="text-xs text-muted-foreground">{model.id}</span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-2">
                      ${((model.pricing?.prompt || 0) / 1000).toFixed(3)}/1K
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default function ProjectPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useAuth();

  const [project, setProject] = useState(null);
  const [experts, setExperts] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExpertId, setSelectedExpertId] = useState(null);

  const selectedExpert = useMemo(() => {
    if (!selectedExpertId) return null;
    return experts.find(e => e.id === selectedExpertId) || null;
  }, [experts, selectedExpertId]);

  // Clarification state
  const [clarificationAnswers, setClarificationAnswers] = useState({});
  const [submittingClarifications, setSubmittingClarifications] = useState(false);

  // Team config state
  const [teamSize, setTeamSize] = useState('normal');
  const [expertConfigs, setExpertConfigs] = useState([]);
  const [configuring, setConfiguring] = useState(false);
  const [projectAccuracyLevel, setProjectAccuracyLevel] = useState('base');

  // Cost estimation state
  const [costEstimate, setCostEstimate] = useState(null);
  const [estimatingCost, setEstimatingCost] = useState(false);

  // Execution state
  const [executionMode, setExecutionMode] = useState('automatic');
  const [executing, setExecuting] = useState(false);
  const [executingExpertId, setExecutingExpertId] = useState(null); // For step-by-step execution

  // Expert questions permission (project-level)
  const [allowExpertQuestions, setAllowExpertQuestions] = useState(false);

  // Dirty-state snapshots (used for regenerate/discard prompts)
  const savedClarificationAnswersRef = useRef(null);
  const savedConfigurationRef = useRef(null);

  // Comment dialog
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentExpert, setCommentExpert] = useState(null);
  const [recalculateMode, setRecalculateMode] = useState('single');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Expert clarification chat
  const [clarificationChatOpen, setClarificationChatOpen] = useState(false);
  const [clarificationMessages, setClarificationMessages] = useState([]);
  const [clarificationInput, setClarificationInput] = useState('');
  const [sendingClarification, setSendingClarification] = useState(false);
  const [clarificationExpert, setClarificationExpert] = useState(null);
  const clarificationEndRef = useRef(null);
  const [updatingSelectedExpertModel, setUpdatingSelectedExpertModel] = useState(false);

  // Chat with Analyzer
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sendingChat, setSendingChat] = useState(false);

  // Results tabs state (controlled for auto-navigation)
  const [activeResultsTab, setActiveResultsTab] = useState('experts');

  // Expert settings dialog
  const [expertSettingsOpen, setExpertSettingsOpen] = useState(false);
  const [selectedExpertForSettings, setSelectedExpertForSettings] = useState(null);

  const settingsExpert = useMemo(() => {
    const role = selectedExpertForSettings?.role;
    if (!role) return null;
    return experts.find(e => e.role === role) || null;
  }, [experts, selectedExpertForSettings]);

  const [expertCustomPrompt, setExpertCustomPrompt] = useState('');
  const [expertMcpServers, setExpertMcpServers] = useState([]);
  const [expertResponseLength, setExpertResponseLength] = useState('medium');
  const [expertLlmModel, setExpertLlmModel] = useState('');
  const [expertOriginalPrompt, setExpertOriginalPrompt] = useState(''); // Store original prompt for reset
  const [newMcpServer, setNewMcpServer] = useState({ name: '', url: '', api_key: '' });
  const [savingExpertSettings, setSavingExpertSettings] = useState(false);
  const [showMcpForm, setShowMcpForm] = useState(false);

  // Expert settings clarification chat (separate from inline chat)
  const [settingsClarificationMessages, setSettingsClarificationMessages] = useState([]);
  const [settingsClarificationInput, setSettingsClarificationInput] = useState('');
  const [settingsSendingClarification, setSettingsSendingClarification] = useState(false);
  const settingsClarificationEndRef = useRef(null);

  // Model suggestions based on user/team preferences
  const [modelSuggestions, setModelSuggestions] = useState({});

  // Team regeneration state
  const [regeneratingTeam, setRegeneratingTeam] = useState(false);

  // Synthesis generation modal
  const [synthesisModalOpen, setSynthesisModalOpen] = useState(false);

  // Edit states
  const [editingName, setEditingName] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedPrompt, setEditedPrompt] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [regenerateAfterPromptSaveOpen, setRegenerateAfterPromptSaveOpen] = useState(false);
  const [regeneratingAfterPromptSave, setRegeneratingAfterPromptSave] = useState(false);

  // Progress navigation confirm (dirty-state)
  const [phaseNavConfirmOpen, setPhaseNavConfirmOpen] = useState(false);
  const [pendingPhaseNav, setPendingPhaseNav] = useState(null); // { targetPhase, stepKey }

  const isComparisonMode = useMemo(() => {
    if (!project) return false;
    // Direct check on mode
    if (['comparison', 'compare'].includes(project.analysis_mode)) return true;
    
    // Heuristic: check expert roles for model names if mode is not explicit
    if (expertConfigs.length > 0) {
      const modelKeywords = ['gpt', 'claude', 'llama', 'gemini', 'mistral', 'jamba', 'qwen', 'phi', 'gemma', 'deepseek'];
      // If ANY expert looks like a model, treat as comparison mode
      const hasModelName = expertConfigs.some(e => 
        modelKeywords.some(kw => e.role.toLowerCase().includes(kw))
      );
      if (hasModelName) return true;
    }
    
    return false;
  }, [project, expertConfigs]);

  const isClarificationDirty = useMemo(() => {
    if (!project || project.phase !== 'clarification') return false;
    const saved = savedClarificationAnswersRef.current || {};
    return JSON.stringify(saved) !== JSON.stringify(clarificationAnswers || {});
  }, [project?.phase, project?.id, clarificationAnswers]);

  const isConfigurationDirty = useMemo(() => {
    if (!project || project.phase !== 'configuration') return false;
    const saved = savedConfigurationRef.current;
    if (!saved) return false;
    const current = {
      teamSize,
      projectAccuracyLevel,
      executionMode,
      allowExpertQuestions,
      expertConfigs,
    };
    return JSON.stringify(saved) !== JSON.stringify(current);
  }, [project?.phase, project?.id, teamSize, projectAccuracyLevel, executionMode, allowExpertQuestions, expertConfigs]);

  const restoreSavedClarifications = () => {
    const saved = savedClarificationAnswersRef.current || {};
    setClarificationAnswers(saved);
  };

  const restoreSavedConfiguration = () => {
    const saved = savedConfigurationRef.current;
    if (!saved) return;
    setTeamSize(saved.teamSize);
    setProjectAccuracyLevel(saved.projectAccuracyLevel);
    setExecutionMode(saved.executionMode);
    setAllowExpertQuestions(saved.allowExpertQuestions);
    setExpertConfigs(Array.isArray(saved.expertConfigs) ? saved.expertConfigs : []);
  };

  // File attachment states for prompt editing
  const [editAttachments, setEditAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const editFileInputRef = React.useRef(null);

  // Constants for file upload
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_FILES = 5;

  // Format cost based on language (EUR for IT, USD for EN)
  const formatCost = (cost) => {
    if (language === 'it') {
      // Convert USD to EUR (approximate rate 0.92)
      const eurCost = cost * 0.92;
      return `€${eurCost.toFixed(4)}`;
    }
    return `$${cost.toFixed(4)}`;
  };

  const loadProject = useCallback(async () => {
    try {
      const [projectRes, expertsRes] = await Promise.all([
        projectApi.get(id),
        projectApi.getExperts(id).catch(() => ({ data: [] })),
      ]);
      setProject(projectRes.data);
      setExperts(expertsRes.data);

      if (typeof projectRes.data.allow_expert_questions === 'boolean') {
        setAllowExpertQuestions(projectRes.data.allow_expert_questions);
      }

      // Sync team size and accuracy level from project (backend is source of truth)
      if (projectRes.data.team_size) {
        setTeamSize(projectRes.data.team_size);
      }
      if (projectRes.data.accuracy_level) {
        setProjectAccuracyLevel(projectRes.data.accuracy_level);
      }

      // Always update expertConfigs from identified_roles when they exist
      // The backend regenerates the correct number of roles based on team_size
      if (projectRes.data.identified_roles?.length > 0) {
        setExpertConfigs(projectRes.data.identified_roles.map(role => ({
          role: role.role,
          llm_model: role.suggested_llm || 'anthropic/claude-3.5-sonnet',
          open_source_only: false,
          description: role.description || '',
          reason_for_llm: role.reason_for_llm || '',
          default_prompt: role.default_prompt || '',
          custom_prompt: role.custom_prompt || '',
          mcp_servers: role.mcp_servers || [],
          response_length: role.response_length || 'medium'
        })));
      }

      // Keep selection stable by id; if missing, default to first expert
      if (expertsRes.data.length > 0) {
        const stillExists = selectedExpertId && expertsRes.data.some(e => e.id === selectedExpertId);
        if (!stillExists) {
          setSelectedExpertId(expertsRes.data[0].id);
        }
      }
    } catch (error) {
      toast.error(language === 'it' ? 'Impossibile caricare il progetto' : 'Failed to load project');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, selectedExpertId, language]);

  const loadModels = useCallback(async () => {
    try {
      const res = await openRouterApi.models();
      const sortedModels = (res.data.models || []).sort((a, b) =>
        (a.name || a.id).localeCompare(b.name || b.id)
      );
      setModels(sortedModels);
    } catch (error) {
      console.error('Failed to load models:', error);
    }
  }, []);

  // Load chat history
  const loadChatHistory = useCallback(async () => {
    if (!id) return;
    try {
      const response = await projectApi.getChatHistory(id);
      setChatHistory(response.data?.chat_history || []);
    } catch (error) {
      console.error('Failed to load chat history');
    }
  }, [id]);

  useEffect(() => {
    loadProject();
    loadModels();
    loadChatHistory();
  }, [loadProject, loadModels, loadChatHistory]);

  // Handle send chat message
  const handleSendChat = async () => {
    if (!chatInput.trim() || sendingChat) return;

    const message = chatInput.trim();
    setChatInput('');
    setSendingChat(true);

    // Add user message immediately
    setChatHistory(prev => [...prev, { role: 'user', content: message }]);

    try {
      const response = await projectApi.chat(id, message);
      setChatHistory(prev => [...prev, { role: 'assistant', content: response.data.response }]);

      if (response.data.experts_consulted?.length > 0) {
        const entityName = isComparisonMode
          ? (language === 'it' ? 'Modelli' : 'Models')
          : (language === 'it' ? 'Esperti' : 'Experts');
        toast.info(
          language === 'it'
            ? `${entityName} consultati: ${response.data.experts_consulted.join(', ')}`
            : `${entityName} consulted: ${response.data.experts_consulted.join(', ')}`
        );
      }
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nell\'invio del messaggio' : 'Failed to send message');
      // Remove the user message if failed
      setChatHistory(prev => prev.slice(0, -1));
    } finally {
      setSendingChat(false);
    }
  };

  // Poll for updates
  useEffect(() => {
    const shouldPoll =
      project?.phase === 'executing' ||
      project?.phase === 'analyzing' ||
      project?.phase === 'recalculating' ||
      project?.phase === 'paused' ||
      experts.some(e => e.status === 'running');

    if (shouldPoll) {
      const interval = setInterval(loadProject, 2000);
      return () => clearInterval(interval);
    }
  }, [project?.phase, experts, loadProject]);

  // Load clarification messages when selected expert changes
  useEffect(() => {
    const loadExpertClarifications = async () => {
      if (selectedExpertId && id) {
        try {
          const response = await projectApi.getExpertClarifications(id, selectedExpertId);
          setClarificationMessages(response.data.clarifications || []);
        } catch (error) {
          setClarificationMessages([]);
        }
      }
    };
    loadExpertClarifications();
  }, [selectedExpertId, id]);

  // Keep clarificationExpert aligned with selection
  useEffect(() => {
    if (selectedExpert) {
      setClarificationExpert(selectedExpert);
    }
  }, [selectedExpert]);

  // Load clarification messages for expert settings dialog
  useEffect(() => {
    const loadSettingsClarifications = async () => {
      if (!expertSettingsOpen) return;
      if (!settingsExpert?.id || !id) {
        setSettingsClarificationMessages([]);
        return;
      }
      try {
        const response = await projectApi.getExpertClarifications(id, settingsExpert.id);
        setSettingsClarificationMessages(response.data.clarifications || []);
      } catch (e) {
        setSettingsClarificationMessages([]);
      }
    };
    loadSettingsClarifications();
  }, [expertSettingsOpen, settingsExpert?.id, id]);

  useEffect(() => {
    if (!expertSettingsOpen) return;
    settingsClarificationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [expertSettingsOpen, settingsClarificationMessages]);

  // Record implicit positive feedback when project completes successfully
  useEffect(() => {
    const recordSuccessFeedback = async () => {
      if (project?.phase === 'completed' && experts.length > 0) {
        // Auto-switch to synthesis tab when project completes
        setActiveResultsTab('synthesis');

        // Record positive feedback for all experts that completed successfully
        for (const expert of experts) {
          if (expert.status === 'completed' && expert.llm_model) {
            try {
              await expertFeedbackApi.record({
                expert_role: expert.role,
                original_model: expert.llm_model,
                final_model: expert.llm_model,
                feedback_type: 'implicit_positive'
              });
            } catch (e) {
              // Silent fail
              console.debug('Feedback recording failed:', e);
            }
          }
        }
      }
    };

    // Only run once when project first becomes completed
    if (project?.phase === 'completed' && !sessionStorage.getItem(`feedback_recorded_${project?.id}`)) {
      sessionStorage.setItem(`feedback_recorded_${project?.id}`, 'true');
      recordSuccessFeedback();
    }
  }, [project?.phase, project?.id, experts]);

  // Note: Local slicing removed - backend handles correct number of roles based on team_size parameter
  // This simplifies state management and prevents race conditions

  // Load model suggestions for expert roles based on user preferences
  useEffect(() => {
    const loadModelSuggestions = async () => {
      if (!expertConfigs.length) return;

      const suggestions = {};
      for (const config of expertConfigs) {
        try {
          const response = await expertFeedbackApi.suggestModel(config.role);
          if (response.data?.model_id) {
            suggestions[config.role] = response.data;
          }
        } catch (e) {
          // Silent fail - suggestions are optional
        }
      }
      setModelSuggestions(suggestions);
    };

    loadModelSuggestions();
  }, [expertConfigs.length > 0 ? expertConfigs.map(c => c.role).join(',') : '']);

  const handleSubmitClarifications = async () => {
    setSubmittingClarifications(true);
    try {
      const answers = Object.entries(clarificationAnswers).map(([questionId, answer]) => ({
        question_id: questionId,
        answer
      }));
      await projectApi.submitClarifications(id, answers);
      toast.success(language === 'it' ? 'Risposte inviate' : 'Answers submitted');
      savedClarificationAnswersRef.current = JSON.parse(JSON.stringify(clarificationAnswers));
      await loadProject();
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nell\'invio delle risposte' : 'Failed to submit answers');
    } finally {
      setSubmittingClarifications(false);
    }
  };

  const handleSkipClarifications = async () => {
    try {
      await projectApi.skipClarifications(id);
      toast.success(language === 'it' ? 'Domande saltate' : 'Questions skipped');
      savedClarificationAnswersRef.current = JSON.parse(JSON.stringify(clarificationAnswers));
      await loadProject();
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel salto delle domande' : 'Failed to skip questions');
    }
  };

  const handleConfigureAndExecute = async () => {
    setConfiguring(true);
    try {
      await projectApi.configureTeam(id, {
        team_size: teamSize,
        custom_count: teamSize === 'custom' ? expertConfigs.length : null,
        execution_mode: executionMode,
        allow_expert_questions: allowExpertQuestions,
        experts: expertConfigs.map(e => ({
          role: e.role,
          llm_model: e.llm_model,
          open_source_only: e.open_source_only,
          custom_prompt: e.custom_prompt,
          mcp_servers: e.mcp_servers,
          response_length: e.response_length,
        })),
      });

      savedConfigurationRef.current = {
        teamSize,
        projectAccuracyLevel,
        executionMode,
        allowExpertQuestions,
        expertConfigs: JSON.parse(JSON.stringify(expertConfigs)),
      };

      await projectApi.execute(id, executionMode);
      toast.success(language === 'it' ? 'Esecuzione avviata' : 'Execution started');
      await loadProject();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (language === 'it'
        ? 'Errore nell\'avvio dell\'esecuzione'
        : 'Failed to start execution');
      toast.error(errorMsg);
      await loadProject();
    } finally {
      setConfiguring(false);
    }
  };

  // Seed snapshots when entering phases (used for discard flow)
  useEffect(() => {
    if (!project) return;
    if (project.phase === 'clarification' && !savedClarificationAnswersRef.current) {
      const existing = {};
      (project.clarification_answers || []).forEach(a => {
        if (a?.question_id) existing[a.question_id] = a.answer || '';
      });
      savedClarificationAnswersRef.current = existing;
    }
    if (project.phase === 'configuration' && !savedConfigurationRef.current) {
      savedConfigurationRef.current = {
        teamSize,
        projectAccuracyLevel,
        executionMode,
        allowExpertQuestions,
        expertConfigs: JSON.parse(JSON.stringify(expertConfigs)),
      };
    }
  }, [project?.phase, project?.id]);

  const handleConfigureTeam = async () => {
    if (expertConfigs.length === 0) {
      const entityName = isComparisonMode
        ? (language === 'it' ? 'un modello' : 'one model')
        : (language === 'it' ? 'un esperto' : 'one expert');
      toast.error(language === 'it' ? `Aggiungi almeno ${entityName}` : `Add at least ${entityName}`);
      return;
    }

    setConfiguring(true);
    try {
      await projectApi.configureTeam(id, {
        team_size: teamSize,
        custom_count: teamSize === 'custom' ? expertConfigs.length : null,
        execution_mode: executionMode,
        allow_expert_questions: allowExpertQuestions,
        experts: expertConfigs.map(e => ({
          role: e.role,
          llm_model: e.llm_model,
          open_source_only: e.open_source_only,
          custom_prompt: e.custom_prompt,
          mcp_servers: e.mcp_servers,
          response_length: e.response_length
        }))
      });
      toast.success(language === 'it' ? 'Team configurato' : 'Team configured');
      savedConfigurationRef.current = {
        teamSize,
        projectAccuracyLevel,
        executionMode,
        allowExpertQuestions,
        expertConfigs: JSON.parse(JSON.stringify(expertConfigs)),
      };
      await loadProject();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (language === 'it' ? 'Errore nella configurazione del team' : 'Failed to configure team');
      toast.error(errorMsg);
    } finally {
      setConfiguring(false);
    }
  };

  const handleEstimateCost = async () => {
    // First save current config
    setEstimatingCost(true);
    try {
      await projectApi.configureTeam(id, {
        team_size: teamSize,
        custom_count: teamSize === 'custom' ? expertConfigs.length : null,
        execution_mode: executionMode,
        allow_expert_questions: allowExpertQuestions,
        experts: expertConfigs.map(e => ({
          role: e.role,
          llm_model: e.llm_model,
          open_source_only: e.open_source_only,
          custom_prompt: e.custom_prompt,
          mcp_servers: e.mcp_servers,
          response_length: e.response_length
        }))
      });

      const res = await projectApi.estimateCost(id);
      setCostEstimate(res.data);
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nella stima dei costi' : 'Failed to estimate cost');
    } finally {
      setEstimatingCost(false);
    }
  };

  const handleExecute = async () => {
    setExecuting(true);
    try {
      await projectApi.execute(id, executionMode);
      toast.success(language === 'it' ? 'Esecuzione avviata' : 'Execution started');
      await loadProject();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (language === 'it' ? 'Errore nell\'avvio dell\'esecuzione' : 'Failed to start execution');
      toast.error(errorMsg);
    } finally {
      setExecuting(false);
    }
  };

  // Execute a single expert in step-by-step mode
  const handleExecuteExpert = async (expertId) => {
    setExecutingExpertId(expertId);
    try {
      await projectApi.executeExpert(id, expertId);
      const entityName = isComparisonMode
        ? (language === 'it' ? 'modello' : 'model')
        : (language === 'it' ? 'esperto' : 'expert');
      toast.success(language === 'it' ? `Esecuzione ${entityName} avviata` : `${entityName.charAt(0).toUpperCase() + entityName.slice(1)} execution started`);
      await loadProject();
    } catch (error) {
      const entityName = isComparisonMode
        ? (language === 'it' ? 'del modello' : 'model')
        : (language === 'it' ? 'dell\'esperto' : 'expert');
      const errorMsg = error.response?.data?.detail || (language === 'it' ? `Errore nell'esecuzione ${entityName}` : `Failed to execute ${entityName}`);
      toast.error(errorMsg);
    } finally {
      setExecutingExpertId(null);
    }
  };

  // Continue execution in step-by-step mode
  const handleContinueExecution = async () => {
    setExecuting(true);
    try {
      const response = await projectApi.continueExecution(id);
      toast.success(language === 'it' ? 'Esecuzione ripresa' : 'Execution resumed');
      await loadProject();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (language === 'it' ? 'Errore nella continuazione' : 'Failed to continue');
      toast.error(errorMsg);
    } finally {
      setExecuting(false);
    }
  };

  const handleComment = async () => {
    if (!commentText.trim() || !commentExpert) return;

    setSubmittingComment(true);
    try {
      await projectApi.addComment(id, {
        expert_id: commentExpert.id,
        comment: commentText,
        recalculate_mode: recalculateMode
      });
      toast.success(language === 'it' ? 'Commento aggiunto, ricalcolo in corso...' : 'Comment added, recalculating...');
      setCommentDialogOpen(false);
      setCommentText('');
      await loadProject();
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nell\'aggiunta del commento' : 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Open clarification chat for an expert
  const openClarificationChat = async (expert) => {
    setClarificationExpert(expert);
    setClarificationChatOpen(true);
    setClarificationInput('');

    try {
      const response = await projectApi.getExpertClarifications(id, expert.id);
      setClarificationMessages(response.data.clarifications || []);
    } catch (error) {
      setClarificationMessages([]);
    }
  };

  // Send clarification message
  const handleSendClarification = async () => {
    if (!clarificationInput.trim() || !clarificationExpert) return;

    setSendingClarification(true);
    const messageContent = clarificationInput;
    setClarificationInput('');

    // Optimistic update - add user message immediately
    const tempUserMsg = {
      id: 'temp-' + Date.now(),
      role: 'user',
      content: messageContent,
      timestamp: new Date().toISOString()
    };
    setClarificationMessages(prev => [...prev, tempUserMsg]);

    try {
      await projectApi.sendExpertClarification(id, clarificationExpert.id, {
        content: messageContent
      });

      const entityName = isComparisonMode
        ? (language === 'it' ? 'il modello' : 'model')
        : (language === 'it' ? 'l\'esperto' : 'expert');
      toast.success(language === 'it' ? `Richiesta inviata, ${entityName} sta rispondendo...` : `Request sent, ${entityName} is responding...`);

      // Poll for response
      const pollForResponse = async () => {
        for (let i = 0; i < 60; i++) { // Max 60 attempts (2 minutes)
          await new Promise(resolve => setTimeout(resolve, 2000));
          const response = await projectApi.getExpertClarifications(id, clarificationExpert.id);
          const messages = response.data.clarifications || [];

          if (messages.length > clarificationMessages.length + 1) {
            setClarificationMessages(messages);
            await loadProject();
            return;
          }

          // Check if expert finished
          if (response.data.expert_status === 'completed' && messages.length > clarificationMessages.length) {
            setClarificationMessages(messages);
            await loadProject();
            return;
          }
        }
      };

      pollForResponse();

    } catch (error) {
      // Remove optimistic message on error
      setClarificationMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      toast.error(language === 'it' ? 'Errore nell\'invio del chiarimento' : 'Failed to send clarification');
    } finally {
      setSendingClarification(false);
    }
  };

  const handleSelectedExpertModelChange = async (newModel) => {
    if (!id || !selectedExpert?.role) return;
    try {
      setUpdatingSelectedExpertModel(true);
      await projectApi.updateExpertSettings(id, selectedExpert.role, { llm_model: newModel });
      await loadProject();
      toast.success(language === 'it' ? 'Modello aggiornato' : 'Model updated');
    } catch (error) {
      console.error('Failed to update expert model:', error);
      toast.error(language === 'it' ? 'Errore aggiornando il modello' : 'Failed to update model');
    } finally {
      setUpdatingSelectedExpertModel(false);
    }
  };

  const handleSendSettingsClarification = async () => {
    if (!settingsClarificationInput.trim() || !settingsExpert) return;

    setSettingsSendingClarification(true);
    const messageContent = settingsClarificationInput;
    setSettingsClarificationInput('');

    const tempUserMsg = {
      id: 'temp-settings-' + Date.now(),
      role: 'user',
      content: messageContent,
      timestamp: new Date().toISOString(),
    };
    setSettingsClarificationMessages(prev => [...prev, tempUserMsg]);

    try {
      await projectApi.sendExpertClarification(id, settingsExpert.id, { content: messageContent });
      const entityName = isComparisonMode
        ? (language === 'it' ? 'il modello' : 'model')
        : (language === 'it' ? 'l\'esperto' : 'expert');
      toast.success(language === 'it' ? `Richiesta inviata, ${entityName} sta rispondendo...` : `Request sent, ${entityName} is responding...`);

      const pollForResponse = async () => {
        for (let i = 0; i < 60; i++) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          const response = await projectApi.getExpertClarifications(id, settingsExpert.id);
          const messages = response.data.clarifications || [];

          if (messages.length > settingsClarificationMessages.length + 1) {
            setSettingsClarificationMessages(messages);
            await loadProject();
            return;
          }

          if (response.data.expert_status === 'completed' && messages.length > settingsClarificationMessages.length) {
            setSettingsClarificationMessages(messages);
            await loadProject();
            return;
          }
        }
      };

      pollForResponse();
    } catch (e) {
      setSettingsClarificationMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      toast.error(language === 'it' ? 'Errore nell\'invio del chiarimento' : 'Failed to send clarification');
    } finally {
      setSettingsSendingClarification(false);
    }
  };

  // Scroll to bottom of clarification chat when messages update
  useEffect(() => {
    clarificationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [clarificationMessages]);

  // Helper to poll until project phase changes from 'analyzing' to 'configuration'
  const waitForRegenerationComplete = async (maxWaitMs = 30000) => {
    const startTime = Date.now();
    const pollInterval = 1000; // 1 second

    while (Date.now() - startTime < maxWaitMs) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      const res = await projectApi.get(id);
      if (res.data.phase === 'configuration' || res.data.phase === 'ready') {
        return res.data;
      }
      // Update project state while waiting
      setProject(res.data);
    }
    // Timeout - return current state anyway
    const res = await projectApi.get(id);
    return res.data;
  };

  // Handle analysis mode change
  const handleAnalysisModeChange = async (newMode) => {
    if (newMode === project.analysis_mode) return;

    setRegeneratingTeam(true);
    try {
      toast.info(language === 'it'
        ? 'Cambio modalità e rigenerazione team...'
        : 'Changing mode and regenerating team...');

      await projectApi.regenerateTeam(id, teamSize, projectAccuracyLevel, newMode);

      // Wait for backend to complete the async regeneration
      await waitForRegenerationComplete();

      toast.success(language === 'it'
        ? 'Modalità aggiornata!'
        : 'Mode updated!');

      await loadProject();
    } catch (error) {
      const errorMsg = error.response?.data?.detail ||
        (language === 'it' ? 'Errore nel cambio modalità' : 'Failed to change mode');
      toast.error(errorMsg);
    } finally {
      setRegeneratingTeam(false);
    }
  };

  // Handle team size change - regenerates the entire team with new size
  const handleTeamSizeChange = async (newSize) => {
    if (newSize === 'custom') {
      // Custom mode: just switch to custom without regenerating
      setTeamSize(newSize);
      return;
    }

    setTeamSize(newSize);
    setRegeneratingTeam(true);

    try {
      toast.info(language === 'it'
        ? 'Rigenerazione team in corso...'
        : 'Regenerating team...');

      await projectApi.regenerateTeam(id, newSize, projectAccuracyLevel, project.analysis_mode);

      // Wait for backend to complete the async regeneration
      await waitForRegenerationComplete();

      toast.success(language === 'it'
        ? 'Team rigenerato con successo!'
        : 'Team regenerated successfully!');

      // Reload project to get new experts
      await loadProject();
    } catch (error) {
      const errorMsg = error.response?.data?.detail ||
        (language === 'it' ? 'Errore nella rigenerazione del team' : 'Failed to regenerate team');
      toast.error(errorMsg);
    } finally {
      setRegeneratingTeam(false);
    }
  };

  // Handle accuracy level change - recalculates models for existing experts
  const handleAccuracyLevelChange = async (newLevel) => {
    setProjectAccuracyLevel(newLevel);
    setRegeneratingTeam(true);

    try {
      // In comparison mode, changing accuracy means we need to select different models entirely
      // so we regenerate the team instead of just recalculating
      if (['comparison', 'compare'].includes(project.analysis_mode)) {
        toast.info(language === 'it'
          ? 'Rigenerazione modelli in corso...'
          : 'Regenerating models...');

        await projectApi.regenerateTeam(id, teamSize, newLevel, project.analysis_mode);

        // Wait for backend to complete the async regeneration
        await waitForRegenerationComplete();

        toast.success(language === 'it'
          ? 'Modelli rigenerati!'
          : 'Models regenerated!');
      } else {
        toast.info(language === 'it'
          ? 'Ricalcolo modelli in corso...'
          : 'Recalculating models...');

        await projectApi.recalculateModels(id, newLevel);

        toast.success(language === 'it'
          ? 'Modelli ricalcolati!'
          : 'Models recalculated!');
      }

      // Reload project to get updated models
      await loadProject();
    } catch (error) {
      const errorMsg = error.response?.data?.detail ||
        (language === 'it' ? 'Errore nel ricalcolo dei modelli' : 'Failed to recalculate models');
      toast.error(errorMsg);
      // Revert to previous level
      setProjectAccuracyLevel(project?.accuracy_level || 'base');
    } finally {
      setRegeneratingTeam(false);
    }
  };

  const handleShare = async () => {
    try {
      const response = await projectApi.share(id);
      const shareUrl = `${window.location.origin}/shared/${response.data.share_token}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success(language === 'it' ? 'Link copiato negli appunti' : 'Share link copied to clipboard');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (language === 'it' ? 'Errore nella creazione del link' : 'Failed to create share link');
      toast.error(errorMsg);
    }
  };

  const handleDelete = async () => {
    try {
      await projectApi.delete(id);
      toast.success(language === 'it' ? 'Progetto eliminato' : 'Project deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nell\'eliminazione' : 'Failed to delete');
    }
  };

  const handleSaveAsTemplate = async () => {
    const templateName = prompt(
      language === 'it' ? 'Nome del template:' : 'Template name:',
      project?.name ? `Template - ${project.name}` : 'Nuovo Template'
    );
    if (!templateName) return;

    try {
      await templateApi.createFromProject(id, templateName);
      toast.success(language === 'it' ? 'Template salvato!' : 'Template saved!');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel salvataggio del template' : 'Failed to save template');
    }
  };

  const handleCopyExpertResponse = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(language === 'it' ? 'Copiato negli appunti' : 'Copied to clipboard');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nella copia' : 'Failed to copy');
    }
  };

  const [regeneratingExpert, setRegeneratingExpert] = useState(false);

  const handleRegenerateExpert = async (expert) => {
    if (!expert?.id) return;
    try {
      setRegeneratingExpert(true);
      await projectApi.regenerateExpert(id, expert.id);
      toast.success(language === 'it' ? 'Rigenerazione avviata...' : 'Regeneration started...');
      // Start polling for completion
      const pollInterval = setInterval(async () => {
        await loadProject();
        const updatedExpert = experts.find(e => e.id === expert.id);
        if (updatedExpert && updatedExpert.status !== 'running') {
          clearInterval(pollInterval);
          setRegeneratingExpert(false);
          if (updatedExpert.response) {
            toast.success(language === 'it' ? 'Risposta rigenerata' : 'Response regenerated');
          }
        }
      }, 2000);
    } catch (error) {
      console.error('Failed to regenerate expert:', error);
      toast.error(language === 'it' ? 'Errore nella rigenerazione' : 'Failed to regenerate');
      setRegeneratingExpert(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await projectApi.export(id, format);
      const mimeTypes = {
        json: 'application/json',
        markdown: 'text/markdown',
        html: 'text/html'
      };
      const blob = new Blob([response.data.content], { type: mimeTypes[format] || 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(language === 'it' ? 'Progetto esportato' : 'Project exported');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nell\'esportazione' : 'Failed to export');
    }
  };

  const handleSaveName = async () => {
    if (!editedName.trim()) return;
    setSavingEdit(true);
    try {
      await projectApi.update(id, { name: editedName.trim() });
      setProject(prev => ({ ...prev, name: editedName.trim() }));
      setEditingName(false);
      toast.success(language === 'it' ? 'Nome aggiornato' : 'Name updated');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel salvataggio' : 'Failed to save');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSavePrompt = async () => {
    if (!editedPrompt.trim()) return;
    setSavingEdit(true);
    try {
      // Combine existing and new attachments
      const allAttachments = [
        ...existingAttachments,
        ...editAttachments
      ];

      await projectApi.update(id, {
        prompt: editedPrompt.trim(),
        attachments: allAttachments
      });
      setProject(prev => ({ ...prev, prompt: editedPrompt.trim() }));
      setEditingPrompt(false);
      setEditAttachments([]);
      toast.success(language === 'it' ? 'Prompt aggiornato' : 'Prompt updated');

      // Ask if user wants to regenerate full analysis
      setRegenerateAfterPromptSaveOpen(true);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (language === 'it' ? 'Errore nel salvataggio' : 'Failed to save');
      toast.error(errorMsg);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleRegenerateAfterPromptSave = async () => {
    if (!id) return;
    setRegeneratingAfterPromptSave(true);
    try {
      const effectiveTeamSize = project?.team_size || teamSize || 'normal';
      const effectiveAccuracy = projectAccuracyLevel || project?.accuracy_level || 'base';

      toast.info(language === 'it' ? 'Rigenerazione analisi completa in corso...' : 'Regenerating full analysis...');
      await projectApi.regenerateTeam(id, effectiveTeamSize, effectiveAccuracy);
      setRegenerateAfterPromptSaveOpen(false);
      await loadProject();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (language === 'it' ? 'Errore nella rigenerazione' : 'Failed to regenerate');
      toast.error(errorMsg);
    } finally {
      setRegeneratingAfterPromptSave(false);
    }
  };

  const startEditingName = () => {
    setEditedName(project.name || '');
    setEditingName(true);
  };

  const startEditingPrompt = () => {
    setEditedPrompt(project.prompt);
    // Load existing attachments if any
    setExistingAttachments(project.attachments || []);
    setEditAttachments([]);
    setEditingPrompt(true);
  };

  const cancelEditingName = () => {
    setEditingName(false);
    setEditedName('');
  };

  const cancelEditingPrompt = () => {
    setEditingPrompt(false);
    setEditedPrompt('');
    setEditAttachments([]);
    setExistingAttachments([]);
  };

  // File handling for prompt editing
  const handleEditFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const totalFiles = existingAttachments.length + editAttachments.length;

    for (const file of files) {
      if (totalFiles + editAttachments.length >= MAX_FILES) {
        toast.error(language === 'it' ? `Massimo ${MAX_FILES} file` : `Maximum ${MAX_FILES} files`);
        break;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error(language === 'it' ? 'File troppo grande (max 10MB)' : 'File too large (max 10MB)');
        continue;
      }

      if (file.size === 0) {
        toast.error(language === 'it' ? 'File vuoto' : 'Empty file');
        continue;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result?.split(',')[1] || '';
        // Use a fallback content_type for markdown files
        let contentType = file.type;
        if (!contentType || contentType === '') {
          const ext = file.name.split('.').pop()?.toLowerCase();
          if (ext === 'md') contentType = 'text/markdown';
          else if (ext === 'json') contentType = 'application/json';
          else if (ext === 'xml') contentType = 'application/xml';
          else contentType = 'text/plain';
        }
        setEditAttachments(prev => [...prev, {
          filename: file.name,
          content_type: contentType,
          content_base64: base64,
          size: file.size,
          isNew: true
        }]);
        toast.success(language === 'it' ? `File "${file.name}" allegato` : `File "${file.name}" attached`);
      };
      reader.onerror = () => {
        toast.error(language === 'it' ? `Errore lettura file "${file.name}"` : `Error reading file "${file.name}"`);
      };
      reader.readAsDataURL(file);
    }

    if (editFileInputRef.current) editFileInputRef.current.value = '';
  };

  const removeExistingAttachment = (index) => {
    setExistingAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const removeNewAttachment = (index) => {
    setEditAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return Image;
    if (type?.includes('pdf') || type?.includes('document') || type?.includes('markdown')) return FileText;
    return File;
  };

  const updateExpertConfig = async (index, field, value) => {
    const oldValue = expertConfigs[index]?.[field];
    // Use the helper
    const isComparison = isComparisonMode;

    setExpertConfigs(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // In comparison mode OR if the role seems to match the old model name (heuristic for manual mode changes)
      // Update the role to match the new model name
      if (field === 'llm_model') {
        const shouldUpdateRole = isComparison || 
          // Heuristic: check if current role looks like the old model name
          (oldValue && updated[index].role.toLowerCase().includes(oldValue.split('/').pop()?.split('-')[0]?.toLowerCase()));

        if (shouldUpdateRole) {
           // Extract model name from the full ID (e.g., "anthropic/claude-3.5-sonnet" -> "Claude 3.5 Sonnet")
          const modelName = value.split('/').pop()
            ?.replace(/-/g, ' ')
            ?.replace(/\b\w/g, l => l.toUpperCase())
            || value;
          updated[index].role = modelName;
        }
      }

      return updated;
    });
    setCostEstimate(null); // Reset cost estimate when config changes

    // Record feedback when user changes LLM model (indicates preference)
    if (field === 'llm_model' && oldValue && oldValue !== value && project?.id) {
      const expertRole = expertConfigs[index]?.role;
      try {
        // Record negative feedback for old model (user changed away from it)
        await expertFeedbackApi.record({
          expert_role: expertRole,
          original_model: oldValue,
          final_model: value,
          feedback_type: 'model_change'
        });
      } catch (e) {
        // Silent fail - feedback recording is not critical
        console.debug('Feedback recording failed:', e);
      }
    }
  };

  const addExpert = () => {
    // Use the helper
    const isComparison = isComparisonMode;
    const defaultModel = 'anthropic/claude-3.5-sonnet';
    const modelName = defaultModel.split('/').pop()
      ?.replace(/-/g, ' ')
      ?.replace(/\b\w/g, l => l.toUpperCase())
      || defaultModel;

    setExpertConfigs(prev => [...prev, {
      role: isComparison
        ? modelName
        : (language === 'it' ? 'Nuovo Esperto' : 'New Expert'),
      llm_model: defaultModel,
      open_source_only: false,
      description: '',
      default_prompt: '',
      custom_prompt: '',
      mcp_servers: [],
      response_length: 'medium'
    }]);
    setCostEstimate(null);
  };

  const removeExpert = (index) => {
    setExpertConfigs(prev => prev.filter((_, i) => i !== index));
    setCostEstimate(null);
  };

  const moveExpert = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= expertConfigs.length) return;

    setExpertConfigs(prev => {
      const newConfigs = [...prev];
      [newConfigs[index], newConfigs[newIndex]] = [newConfigs[newIndex], newConfigs[index]];
      return newConfigs;
    });
    setCostEstimate(null);
  };

  // Expert settings functions
  const openExpertSettings = (config, index) => {
    setSelectedExpertForSettings({ ...config, index });
    // Store original prompt (default_prompt from manager)
    const originalPrompt = config.default_prompt || '';
    setExpertOriginalPrompt(originalPrompt);
    // Show current custom prompt if set, otherwise the original
    setExpertCustomPrompt(config.custom_prompt || originalPrompt);
    setExpertMcpServers(config.mcp_servers || []);
    setExpertResponseLength(config.response_length || 'medium');
    setExpertLlmModel(config.llm_model || '');
    setShowMcpForm(false);
    setExpertSettingsOpen(true);
  };

  const addMcpServerToExpert = () => {
    if (!newMcpServer.name.trim() || !newMcpServer.url.trim()) {
      toast.error(language === 'it' ? 'Nome e URL sono obbligatori' : 'Name and URL are required');
      return;
    }
    setExpertMcpServers(prev => [...prev, { ...newMcpServer }]);
    setNewMcpServer({ name: '', url: '', api_key: '' });
    setShowMcpForm(false);
  };

  const removeMcpServerFromExpert = (index) => {
    setExpertMcpServers(prev => prev.filter((_, i) => i !== index));
  };

  const saveExpertSettings = async () => {
    if (!selectedExpertForSettings) return;

    setSavingExpertSettings(true);
    try {
      // Update local config
      const updatedConfig = {
        ...selectedExpertForSettings,
        custom_prompt: expertCustomPrompt,
        mcp_servers: expertMcpServers,
        response_length: expertResponseLength,
        llm_model: expertLlmModel
      };

      setExpertConfigs(prev => {
        const newConfigs = [...prev];
        newConfigs[selectedExpertForSettings.index] = updatedConfig;
        return newConfigs;
      });

      // Save to backend if project exists
      if (project?.id) {
        await projectApi.updateExpertSettings(project.id, selectedExpertForSettings.role, {
          custom_prompt: expertCustomPrompt,
          mcp_servers: expertMcpServers,
          response_length: expertResponseLength,
          llm_model: expertLlmModel
        });
      }

      toast.success(language === 'it' ? 'Impostazioni esperto salvate' : 'Expert settings saved');
      setExpertSettingsOpen(false);
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel salvataggio' : 'Failed to save');
    } finally {
      setSavingExpertSettings(false);
    }
  };

  // Reset expert prompt to original from manager (not comment)
  const resetExpertPromptToDefault = () => {
    if (expertOriginalPrompt) {
      setExpertCustomPrompt(expertOriginalPrompt);
      toast.info(language === 'it' ? 'Prompt ripristinato al valore del Manager' : 'Prompt reset to Manager value');
    } else {
      toast.error(language === 'it' ? 'Nessun prompt originale disponibile' : 'No original prompt available');
    }
  };

  // Handle progress step click - navigate to that phase for editing
  const handleStepClick = async (targetPhase, stepKey) => {
    // For completed projects, show info about what's in that phase
    if (project.phase === 'completed') {
      if (stepKey === 'configuration') {
        // Show team configuration info
        const tabName = ['comparison', 'compare'].includes(project.analysis_mode)
          ? (language === 'it' ? 'Modelli' : 'Models')
          : (language === 'it' ? 'Esperti' : 'Experts');
        toast.info(language === 'it'
          ? `Visualizzazione configurazione team: vai alla tab ${tabName} per vedere la configurazione`
          : `Team configuration view: go to ${tabName} tab to see the configuration`);
      } else if (stepKey === 'executing' || stepKey === 'completed') {
        // Scroll to results
        const tabName = ['comparison', 'compare'].includes(project.analysis_mode)
          ? (language === 'it' ? 'Modelli' : 'Models')
          : (language === 'it' ? 'Esperti' : 'Experts');
        toast.info(language === 'it'
          ? `Vai alla tab ${tabName} o Sintesi per vedere i risultati`
          : `Go to ${tabName} or Synthesis tab to see the results`);
      }
      return;
    }

    // If current section has unsaved changes, confirm before navigating away
    if (project.phase === 'clarification' && targetPhase !== 'clarification' && isClarificationDirty) {
      setPendingPhaseNav({ targetPhase, stepKey });
      setPhaseNavConfirmOpen(true);
      return;
    }
    if (project.phase === 'configuration' && targetPhase !== 'configuration' && isConfigurationDirty) {
      setPendingPhaseNav({ targetPhase, stepKey });
      setPhaseNavConfirmOpen(true);
      return;
    }

    // For non-completed projects, allow navigation to previous phases
    try {
      const response = await projectApi.update(id, { phase: targetPhase });
      toast.success(language === 'it'
        ? `Fase cambiata a "${stepKey}". Ora puoi modificare.`
        : `Phase changed to "${stepKey}". You can now make changes.`);
      await loadProject();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (language === 'it'
        ? 'Impossibile cambiare fase'
        : 'Cannot change phase');
      toast.error(errorMsg);
    }
  };

  const handlePhaseNavDiscard = async () => {
    if (!pendingPhaseNav) return;
    if (project?.phase === 'clarification') restoreSavedClarifications();
    if (project?.phase === 'configuration') restoreSavedConfiguration();
    setPhaseNavConfirmOpen(false);

    try {
      await projectApi.update(id, { phase: pendingPhaseNav.targetPhase });
      await loadProject();
    } catch (e) {
      toast.error(language === 'it' ? 'Impossibile cambiare fase' : 'Cannot change phase');
    } finally {
      setPendingPhaseNav(null);
    }
  };

  const handlePhaseNavRegenerate = async () => {
    if (!pendingPhaseNav) return;
    setPhaseNavConfirmOpen(false);

    try {
      if (project?.phase === 'clarification') {
        await handleSubmitClarifications();
        // From clarifications you can only proceed to configuration
        if (pendingPhaseNav.targetPhase !== 'configuration') {
          toast.info(language === 'it'
            ? 'Vai in "Configurazione" per proseguire.'
            : 'Go to "Configuration" to continue.');
        }
      } else if (project?.phase === 'configuration') {
        // Regenerate from configuration: apply config and start execution
        await handleConfigureAndExecute();
      }
    } finally {
      setPendingPhaseNav(null);
    }
  };

  // Continue execution and complete all remaining experts automatically
  const handleContinueAndFinish = async () => {
    setExecuting(true);
    try {
      // Switch to automatic mode and continue
      await projectApi.execute(id, 'automatic');
      toast.success(language === 'it' ? 'Esecuzione automatica avviata' : 'Automatic execution started');
      await loadProject();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (language === 'it' ? 'Errore nell\'esecuzione' : 'Execution failed');
      toast.error(errorMsg);
    } finally {
      setExecuting(false);
    }
  };

  // Check if all experts are completed (for step-by-step mode)
  const allExpertsCompleted = experts.length > 0 && experts.every(e => e.status === 'completed');
  const hasMorePendingExperts = experts.some(e => e.status === 'pending');

  // Generate final synthesis
  const handleGenerateFinalSynthesis = async () => {
    setExecuting(true);
    setSynthesisModalOpen(true);
    try {
      await projectApi.addComment(id, {
        expert_id: 'orchestrator',
        comment: 'Generate final synthesis after all experts completed',
        recalculate_mode: 'orchestrator'
      });
      // Keep polling until synthesis is complete
      const pollSynthesis = async () => {
        const res = await projectApi.get(id);
        if (res.data.final_synthesis || res.data.phase === 'completed') {
          setProject(res.data);
          setSynthesisModalOpen(false);
          setExecuting(false);
          toast.success(language === 'it' ? 'Sintesi finale completata!' : 'Final synthesis completed!');
        } else if (res.data.phase === 'error') {
          setSynthesisModalOpen(false);
          setExecuting(false);
          toast.error(language === 'it' ? 'Errore nella generazione' : 'Generation failed');
        } else {
          setProject(res.data);
          setTimeout(pollSynthesis, 2000);
        }
      };
      await pollSynthesis();
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nella generazione' : 'Generation failed');
      setSynthesisModalOpen(false);
      setExecuting(false);
    }
  };

  // Interrupt synthesis generation
  const handleInterruptSynthesis = () => {
    setSynthesisModalOpen(false);
    setExecuting(false);
    toast.info(language === 'it' ? 'Generazione interrotta (il processo potrebbe continuare in background)' : 'Generation interrupted (process may continue in background)');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!project) return null;

  const isProcessing = ['analyzing', 'executing', 'recalculating'].includes(project.phase);
  const canEdit = !['executing', 'completed'].includes(project.phase);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            {/* Project Name (editable) */}
            <div className="flex items-center gap-3">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="text-2xl font-heading font-bold h-10 w-80"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') cancelEditingName();
                    }}
                  />
                  <Button variant="ghost" size="icon" onClick={handleSaveName} disabled={savingEdit}>
                    {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 text-success" />}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={cancelEditingName}>
                    <X className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ) : (
                <>
                  <h1 className="text-2xl font-heading font-bold tracking-tight">
                    {project.name || project.prompt?.slice(0, 50) + '...'}
                  </h1>
                  {canEdit && (
                    <Button variant="ghost" size="icon" onClick={startEditingName} className="h-8 w-8">
                      <Pencil className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  )}
                  {project.version > 1 && (
                    <Badge variant="outline" className="font-code">v{project.version}</Badge>
                  )}
                </>
              )}
            </div>
            {/* Subtitle with date and cost */}
            <p className="text-sm text-muted-foreground font-code mt-1">
              {language === 'it' ? 'Progetto' : 'Project'} • {new Date(project.created_at).toLocaleString(language === 'it' ? 'it-IT' : 'en-US')}
              {project.total_cost > 0 && (
                <span className="ml-3">• {language === 'it' ? 'Costo finora' : 'Cost so far'}: {formatCost(project.total_cost)}</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {project.phase === 'completed' && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" onClick={handleShare} className="gap-2" data-testid="share-btn">
                    <Share2 className="w-4 h-4" />
                    {t('share')}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{language === 'it' ? 'Crea un link condivisibile' : 'Create a shareable link'}</p>
                </TooltipContent>
              </Tooltip>
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="gap-2" data-testid="export-btn">
                        <Download className="w-4 h-4" />
                        {t('export')}
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{language === 'it' ? 'Esporta report' : 'Export report'}</p>
                  </TooltipContent>
                </Tooltip>
                <DropdownMenuContent align="end" className="bg-card border-border">
                  <DropdownMenuItem onClick={() => handleExport('markdown')}>
                    <FileText className="w-4 h-4 mr-2" />
                    Markdown (.md)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('html')}>
                    <Globe className="w-4 h-4 mr-2" />
                    HTML / PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('json')}>
                    <Hash className="w-4 h-4 mr-2" />
                    JSON
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}

          {/* Save as Template Button */}
          {project.phase === 'completed' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" onClick={handleSaveAsTemplate}>
                  <Bookmark className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="font-medium">{language === 'it' ? 'Salva come Template' : 'Save as Template'}</p>
                <p className="text-xs text-muted-foreground">
                  {language === 'it'
                    ? 'Salva la configurazione di questo progetto (esperti, modelli, accuratezza) per riutilizzarla in futuro'
                    : 'Save this project configuration (experts, models, accuracy) for future reuse'}
                </p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Delete Project Button */}
          <AlertDialog>
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="icon" className="text-destructive hover:text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
              </TooltipTrigger>
              <TooltipContent>
                <p>{language === 'it' ? 'Elimina progetto' : 'Delete project'}</p>
              </TooltipContent>
            </Tooltip>
            <AlertDialogContent className="bg-card border-border">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {language === 'it' ? 'Eliminare il progetto?' : 'Delete project?'}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {language === 'it'
                    ? 'Questa azione non può essere annullata. Il progetto e tutti i pareri degli esperti verranno eliminati permanentemente.'
                    : 'This action cannot be undone. The project and all expert opinions will be permanently deleted.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>
                  {language === 'it' ? 'Annulla' : 'Cancel'}
                </AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
                  {language === 'it' ? 'Elimina' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Progress Steps */}
      <ProgressSteps phase={project.phase} language={language} />

      <AlertDialog open={phaseNavConfirmOpen} onOpenChange={setPhaseNavConfirmOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {language === 'it' ? 'Modifiche non salvate' : 'Unsaved changes'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'it'
                ? 'Hai modifiche non salvate. Vuoi rigenerare ripartendo da questo punto, oppure annullare le modifiche e ripristinare l’ultimo stato completato?'
                : 'You have unsaved changes. Do you want to regenerate from this point, or discard changes and restore the last completed state?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingPhaseNav(null)}>
              {language === 'it' ? 'Annulla' : 'Cancel'}
            </AlertDialogCancel>
            <Button variant="outline" onClick={handlePhaseNavDiscard}>
              {language === 'it' ? 'Annulla modifiche' : 'Discard changes'}
            </Button>
            <AlertDialogAction onClick={handlePhaseNavRegenerate}>
              {language === 'it' ? 'Rigenera' : 'Regenerate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={regenerateAfterPromptSaveOpen} onOpenChange={setRegenerateAfterPromptSaveOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {language === 'it' ? 'Rigenerare l\'analisi completa?' : 'Regenerate full analysis?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'it'
                ? 'Hai aggiornato il prompt originale. Vuoi rigenerare Manager e Team per riallineare tutta l\'analisi al nuovo prompt?'
                : 'You updated the original prompt. Do you want to rerun Manager and regenerate the Team to align the full analysis with the new prompt?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={regeneratingAfterPromptSave}>
              {language === 'it' ? 'No, solo salva' : 'No, just save'}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleRegenerateAfterPromptSave} disabled={regeneratingAfterPromptSave}>
              {regeneratingAfterPromptSave
                ? (language === 'it' ? 'Rigenerazione...' : 'Regenerating...')
                : (language === 'it' ? 'Sì, rigenera' : 'Yes, regenerate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Original Prompt (editable) */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {language === 'it' ? 'Prompt Originale' : 'Original Prompt'}
            </CardTitle>
            {canEdit && !editingPrompt && (
              <Button variant="ghost" size="sm" onClick={startEditingPrompt} className="gap-1 h-7">
                <Pencil className="w-3 h-3" />
                {language === 'it' ? 'Modifica' : 'Edit'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {editingPrompt ? (
            <div className="space-y-4">
              <Textarea
                value={editedPrompt}
                onChange={(e) => setEditedPrompt(e.target.value)}
                className="min-h-32 bg-background"
                autoFocus
              />

              {/* Existing Attachments */}
              {existingAttachments.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    {language === 'it' ? 'File allegati esistenti:' : 'Existing attachments:'}
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {existingAttachments.map((file, idx) => {
                      const FileIcon = getFileIcon(file.content_type);
                      return (
                        <div key={`existing-${idx}`} className="flex items-center gap-1 bg-muted rounded-md px-2 py-1 text-xs">
                          <FileIcon className="w-3 h-3" />
                          <span className="max-w-[120px] truncate">{file.filename}</span>
                          <button
                            onClick={() => removeExistingAttachment(idx)}
                            className="hover:text-destructive ml-1"
                            title={language === 'it' ? 'Rimuovi' : 'Remove'}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* New Attachments */}
              {editAttachments.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    {language === 'it' ? 'Nuovi file da aggiungere:' : 'New files to add:'}
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {editAttachments.map((file, idx) => {
                      const FileIcon = getFileIcon(file.content_type);
                      return (
                        <div key={`new-${idx}`} className="flex items-center gap-1 bg-primary/10 border border-primary/30 rounded-md px-2 py-1 text-xs">
                          <FileIcon className="w-3 h-3 text-primary" />
                          <span className="max-w-[120px] truncate">{file.filename}</span>
                          <Badge variant="outline" className="text-[10px] px-1 py-0">NEW</Badge>
                          <button
                            onClick={() => removeNewAttachment(idx)}
                            className="hover:text-destructive ml-1"
                            title={language === 'it' ? 'Rimuovi' : 'Remove'}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* File Upload Input */}
              <input
                ref={editFileInputRef}
                type="file"
                multiple
                accept=".pdf,.txt,.csv,.docx,.png,.jpg,.jpeg,.webp,.md,.json,.xml"
                onChange={handleEditFileSelect}
                className="hidden"
              />

              {/* Action Buttons */}
              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => editFileInputRef.current?.click()}
                  disabled={(existingAttachments.length + editAttachments.length) >= MAX_FILES}
                  className="gap-1"
                >
                  <Paperclip className="w-3 h-3" />
                  {language === 'it' ? 'Allega File' : 'Attach File'}
                </Button>
                <span className="text-xs text-muted-foreground">
                  {existingAttachments.length + editAttachments.length}/{MAX_FILES} file
                </span>
                <div className="flex-1" />
                <Button onClick={handleSavePrompt} disabled={savingEdit} size="sm" className="gap-1">
                  {savingEdit ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  {language === 'it' ? 'Salva' : 'Save'}
                </Button>
                <Button variant="outline" onClick={cancelEditingPrompt} size="sm" className="gap-1">
                  <X className="w-3 h-3" />
                  {language === 'it' ? 'Annulla' : 'Cancel'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-foreground whitespace-pre-wrap">{project.prompt}</p>

              {/* Show existing attachments in view mode */}
              {project.attachments && project.attachments.length > 0 && (
                <div className="pt-3 border-t border-border">
                  <Label className="text-xs text-muted-foreground mb-2 block">
                    {language === 'it' ? 'File allegati:' : 'Attached files:'}
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {project.attachments.map((file, idx) => {
                      const FileIcon = getFileIcon(file.content_type);
                      return (
                        <div key={idx} className="flex items-center gap-1 bg-muted rounded-md px-2 py-1 text-xs">
                          <FileIcon className="w-3 h-3" />
                          <span className="max-w-[150px] truncate">{file.filename}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Phase: Analyzing - Manager Card Hidden as per user request, spinner moved to team size buttons */}
      
      {/* Phase: Clarification Questions */}
      {project.phase === 'clarification' && project.clarification_questions?.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              {t('clarifications')}
            </CardTitle>
            <CardDescription>
              {language === 'it'
                ? 'Rispondi a queste domande per migliorare l\'analisi (opzionale)'
                : 'Answer these questions to improve the analysis (optional)'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {project.clarification_questions.map((q) => (
              <div key={q.id} className="space-y-2">
                <Label className="text-foreground">{q.question}</Label>
                <Textarea
                  placeholder={language === 'it' ? 'La tua risposta...' : 'Your answer...'}
                  value={clarificationAnswers[q.id] || ''}
                  onChange={(e) => setClarificationAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                  className="bg-background"
                  data-testid={`clarification-${q.id}`}
                />
              </div>
            ))}
            <div className="flex gap-3 pt-4">
              <Button onClick={handleSkipClarifications} variant="outline" data-testid="skip-clarifications-btn">
                {t('skip')}
              </Button>
              <Button onClick={handleSubmitClarifications} disabled={submittingClarifications} className="gap-2" data-testid="submit-clarifications-btn">
                {submittingClarifications ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {t('submitAnswers')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phase: Team Configuration */}
      {(project.phase === 'configuration' || project.phase === 'ready' || project.phase === 'analyzing' || regeneratingTeam) && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-accent" />
              {isComparisonMode
                ? (language === 'it' ? 'Configura Modelli AI' : 'Configure AI Models')
                : t('configureTeam')}
            </CardTitle>
            <CardDescription>
              {isComparisonMode
                ? (language === 'it'
                  ? `${expertConfigs.length} modelli AI selezionati. Tutti risponderanno alla STESSA domanda per permetterti di confrontare le risposte.`
                  : `${expertConfigs.length} AI models selected. All will answer the SAME question so you can compare responses.`)
                : (language === 'it'
                  ? `${(project.identified_roles?.length ?? expertConfigs.length)} esperti identificati con LLM suggeriti dal Manager. Puoi modificarli se necessario.`
                  : `${(project.identified_roles?.length ?? expertConfigs.length)} experts identified with LLMs suggested by Manager. You can modify them if needed.`)}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Team Size */}
            <div className="space-y-2">
              <Label>{t('teamSize')}</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  { value: 'simplified', label: language === 'it' ? 'Semplificato (2-3)' : 'Simplified (2-3)' },
                  { value: 'normal', label: language === 'it' ? 'Normale (4-7)' : 'Normal (4-7)' },
                  { value: 'extended', label: language === 'it' ? 'Esteso (8-15)' : 'Extended (8-15)' },
                  { value: 'custom', label: language === 'it' ? 'Personalizzato' : 'Custom' },
                ].map(opt => (
                  <Button
                    key={opt.value}
                    variant={teamSize === opt.value ? 'default' : 'outline'}
                    onClick={() => handleTeamSizeChange(opt.value)}
                    className="text-sm"
                    disabled={regeneratingTeam || project.phase === 'analyzing'}
                    data-testid={`team-size-${opt.value}`}
                  >
                    {(regeneratingTeam || project.phase === 'analyzing') && teamSize !== opt.value ? null : opt.label}
                    {(regeneratingTeam || project.phase === 'analyzing') && teamSize === opt.value && <Loader2 className="w-4 h-4 animate-spin ml-1" />}
                  </Button>
                ))}
              </div>
            </div>

            {/* Project Accuracy Level */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label>{language === 'it' ? 'Livello Accuratezza' : 'Accuracy Level'}</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="w-4 h-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">{language === 'it'
                      ? 'Influenza la selezione dei modelli LLM. Livelli più alti = modelli migliori ma più costosi.'
                      : 'Influences LLM model selection. Higher levels = better but more expensive models.'}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { value: 'economic', label: '💰', tooltip: language === 'it' ? 'Economico' : 'Economic' },
                  { value: 'base', label: '📊', tooltip: language === 'it' ? 'Base' : 'Basic' },
                  { value: 'intermediate', label: '⚖️', tooltip: language === 'it' ? 'Intermedio' : 'Intermediate' },
                  { value: 'advanced', label: '🚀', tooltip: language === 'it' ? 'Avanzato' : 'Advanced' },
                  { value: 'maximum', label: '🧠', tooltip: language === 'it' ? 'Massimo' : 'Maximum' },
                ].map(opt => (
                  <Tooltip key={opt.value}>
                    <TooltipTrigger asChild>
                      <Button
                        variant={projectAccuracyLevel === opt.value ? 'default' : 'outline'}
                        onClick={() => handleAccuracyLevelChange(opt.value)}
                        className="text-lg"
                        disabled={regeneratingTeam}
                        data-testid={`accuracy-${opt.value}`}
                      >
                        {regeneratingTeam && projectAccuracyLevel === opt.value
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : opt.label}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{opt.tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </div>

            {/* Expert Configs / AI Models for comparison */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>
                  {isComparisonMode
                    ? (language === 'it' ? `Modelli AI (${expertConfigs.length})` : `AI Models (${expertConfigs.length})`)
                    : (language === 'it' ? `Esperti (${expertConfigs.length})` : `Experts (${expertConfigs.length})`)}
                </Label>
                {teamSize === 'custom' && (
                  <Button variant="outline" size="sm" onClick={addExpert} className="gap-1" data-testid="add-expert-btn">
                    <Plus className="w-3 h-3" />
                    {isComparisonMode
                      ? (language === 'it' ? 'Aggiungi Modello' : 'Add Model')
                      : (language === 'it' ? 'Aggiungi' : 'Add')}
                  </Button>
                )}
              </div>

              {expertConfigs.map((config, index) => (
                <div key={`expert-${index}-${config.llm_model || 'default'}`} className="p-4 rounded-lg border border-border bg-background space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1">
                      {/* Reorder buttons - always show when there are multiple experts */}
                      {expertConfigs.length > 1 && (
                        <div className="flex flex-col gap-0.5 mr-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5"
                            onClick={() => moveExpert(index, 'up')}
                            disabled={index === 0}
                            title={language === 'it' ? 'Sposta su' : 'Move up'}
                          >
                            <ChevronUp className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5"
                            onClick={() => moveExpert(index, 'down')}
                            disabled={index === expertConfigs.length - 1}
                            title={language === 'it' ? 'Sposta giù' : 'Move down'}
                          >
                            <ChevronDown className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                      {/* In comparison mode, show model icon; in specialists mode, show user icon */}
                      {isComparisonMode ? (
                        <Cpu className="w-4 h-4 text-primary" />
                      ) : (
                        <User className="w-4 h-4 text-muted-foreground" />
                      )}
                      {/* In comparison mode, show model name as title; in specialists mode, show role */}
                      {isComparisonMode ? (
                        <span className="font-medium text-primary">
                          {config.llm_model?.split('/').pop()
                            ?.replace(/-/g, ' ')
                            ?.replace(/\b\w/g, l => l.toUpperCase()) || `Modello ${index + 1}`}
                        </span>
                      ) : (
                        teamSize === 'custom' ? (
                          <Input
                            value={config.role}
                            onChange={(e) => updateExpertConfig(index, 'role', e.target.value)}
                            className="bg-card h-8 text-sm font-medium"
                            data-testid={`expert-role-${index}`}
                          />
                        ) : (
                          <span className="font-medium">{config.role}</span>
                        )
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Expert Settings Button - hide in comparison mode */}
                      {!isComparisonMode && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openExpertSettings(config, index)}
                            >
                              <Settings className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{language === 'it'
                              ? (isComparisonMode ? 'Impostazioni modello (prompt, MCP)' : 'Impostazioni esperto (prompt, MCP)')
                              : (isComparisonMode ? 'Model settings (prompt, MCP)' : 'Expert settings (prompt, MCP)')}</p>
                          </TooltipContent>
                        </Tooltip>
                      )}

                      <div className="flex items-center gap-2">
                        <Label htmlFor={`oss-${index}`} className="text-xs text-muted-foreground whitespace-nowrap">
                          Open Source
                        </Label>
                        <Switch
                          id={`oss-${index}`}
                          checked={config.open_source_only}
                          onCheckedChange={(checked) => updateExpertConfig(index, 'open_source_only', checked)}
                        />
                      </div>
                      {teamSize === 'custom' && expertConfigs.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => removeExpert(index)}
                          data-testid={`remove-expert-${index}`}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Hide description and reason in comparison mode */}
                  {!isComparisonMode && config.description && (
                    <p className="text-xs text-muted-foreground">{config.description}</p>
                  )}
                  {!isComparisonMode && config.reason_for_llm && (
                    <p className="text-xs text-primary/70 italic">
                      {language === 'it' ? 'Motivazione Manager: ' : 'Manager reason: '}{config.reason_for_llm}
                    </p>
                  )}
                  {/* Show custom prompt indicator - only in specialists mode */}
                  {!isComparisonMode && config.custom_prompt && (
                    <p className="text-xs text-success flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      {language === 'it' ? 'Prompt personalizzato' : 'Custom prompt set'}
                    </p>
                  )}
                  {/* Show MCP servers indicator - only in specialists mode */}
                  {!isComparisonMode && config.mcp_servers?.length > 0 && (
                    <p className="text-xs text-info flex items-center gap-1">
                      <Server className="w-3 h-3" />
                      {config.mcp_servers.length} MCP {language === 'it' ? 'configurati' : 'configured'}
                    </p>
                  )}
                  {/* Show response length indicator */}
                  {config.response_length && config.response_length !== 'medium' && (
                    <p className="text-xs text-warning flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      {config.response_length === 'brief'
                        ? (language === 'it' ? 'Risposta breve' : 'Brief response')
                        : (language === 'it' ? 'Risposta completa' : 'Complete response')}
                    </p>
                  )}
                  {/* Show model suggestion based on preferences */}
                  {modelSuggestions[config.role] && (
                    <div className="text-xs bg-primary/10 rounded p-2 space-y-1">
                      <p className="flex items-center gap-1 text-primary">
                        <Lightbulb className="w-3 h-3" />
                        <span className="font-medium">
                          {language === 'it' ? 'Suggerimento:' : 'Suggestion:'}
                        </span>
                      </p>
                      <p className="text-muted-foreground">
                        {modelSuggestions[config.role].message}
                      </p>
                      {modelSuggestions[config.role].model_id && modelSuggestions[config.role].model_id !== config.llm_model && (
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-xs"
                          onClick={() => updateExpertConfig(index, 'llm_model', modelSuggestions[config.role].model_id)}
                        >
                          {language === 'it' ? 'Usa modello suggerito: ' : 'Use suggested model: '}
                          {modelSuggestions[config.role].model_id.split('/').pop()}
                        </Button>
                      )}
                    </div>
                  )}
                  <ModelSelector
                    value={config.llm_model}
                    onChange={(value) => updateExpertConfig(index, 'llm_model', value)}
                    models={models}
                    openSourceOnly={config.open_source_only}
                    language={language}
                  />
                </div>
              ))}
            </div>

            {/* Cost Estimation */}
            <div className="space-y-3 pt-4 border-t border-border">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  {language === 'it' ? 'Stima Costi' : 'Cost Estimate'}
                </Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEstimateCost}
                  disabled={estimatingCost || expertConfigs.length === 0}
                  className="gap-2"
                  data-testid="estimate-cost-btn"
                >
                  {estimatingCost ? <Loader2 className="w-3 h-3 animate-spin" /> : <Calculator className="w-3 h-3" />}
                  {language === 'it' ? 'Calcola' : 'Calculate'}
                </Button>
              </div>

              {costEstimate && (
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {language === 'it' ? 'Costo Stimato Totale:' : 'Estimated Total Cost:'}
                    </span>
                    <span className="font-heading font-bold text-lg text-primary">
                      {formatCost(costEstimate.total.min)} - {formatCost(costEstimate.total.max)}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    {costEstimate.experts.map((e, i) => (
                      <div key={i} className="flex justify-between">
                        <span>{e.role}</span>
                        <span>{formatCost(e.min_cost)} - {formatCost(e.max_cost)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between pt-1 border-t border-border">
                      <span>{language === 'it' ? 'Manager del Progetto' : 'Project Manager'}</span>
                      <span>{formatCost(costEstimate.project_manager?.min_cost || costEstimate.orchestrator?.min_cost)} - {formatCost(costEstimate.project_manager?.max_cost || costEstimate.orchestrator?.max_cost)}</span>
                    </div>
                  </div>
                  <p className="text-xs text-warning italic">
                    ⚠️ {costEstimate.disclaimer || (language === 'it'
                      ? 'Questa è una stima approssimativa (±50%). Il costo effettivo dipende dalla lunghezza delle risposte.'
                      : 'This is an approximate estimate (±50%). Actual cost depends on response length.')}
                  </p>
                </div>
              )}
            </div>

            {/* Execution Mode + Expert Questions */}
            <div className="space-y-3 pt-4 border-t border-border">
              <div className="flex items-center justify-between gap-4">
                <Label>{language === 'it' ? 'Modalità esecuzione' : 'Execution mode'}</Label>
                <div className="flex gap-2">
                  <Button
                    variant={executionMode === 'automatic' ? 'default' : 'outline'}
                    onClick={() => setExecutionMode('automatic')}
                    size="sm"
                    disabled={configuring}
                  >
                    {t('automatic')}
                  </Button>
                  <Button
                    variant={executionMode === 'step_by_step' ? 'default' : 'outline'}
                    onClick={() => setExecutionMode('step_by_step')}
                    size="sm"
                    disabled={configuring}
                  >
                    {t('stepByStep')}
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label>{language === 'it'
                    ? (isComparisonMode ? 'Permetti domande dai modelli' : 'Permetti domande dagli esperti')
                    : (isComparisonMode ? 'Allow model questions' : 'Allow expert questions')}</Label>
                  <p className="text-xs text-muted-foreground">
                    {language === 'it'
                      ? (isComparisonMode
                        ? 'Se attivo, i modelli possono inserire una sezione DOMANDE quando mancano informazioni.'
                        : 'Se attivo, gli esperti possono inserire una sezione DOMANDE quando mancano informazioni.')
                      : (isComparisonMode
                        ? 'If enabled, models may add a QUESTIONS section when information is missing.'
                        : 'If enabled, experts may add a QUESTIONS section when information is missing.')}
                  </p>
                </div>
                <Switch
                  checked={allowExpertQuestions}
                  onCheckedChange={(v) => setAllowExpertQuestions(!!v)}
                />
              </div>
            </div>

            <Button onClick={handleConfigureAndExecute} disabled={configuring || expertConfigs.length === 0} className="w-full gap-2" data-testid="configure-team-btn">
              {configuring ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
              {language === 'it' ? 'Conferma e avvia esecuzione' : 'Confirm & start execution'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Phase: Executing */}
      {project.phase === 'executing' && (
        <Card className="bg-warning/5 border-warning/30">
          <CardContent className="py-8">
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="relative">
                <Sparkles className="w-16 h-16 text-warning animate-pulse" />
                <Loader2 className="w-8 h-8 text-warning animate-spin absolute -bottom-1 -right-1" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-heading font-semibold text-warning">
                  {isComparisonMode
                    ? (language === 'it' ? 'Modelli AI in Esecuzione...' : 'AI Models Executing...')
                    : (language === 'it' ? 'Esperti in Esecuzione...' : 'Experts Executing...')}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {isComparisonMode
                    ? (language === 'it' ? 'I modelli AI stanno rispondendo alla tua domanda' : 'AI models are answering your question')
                    : (language === 'it' ? 'Gli esperti stanno analizzando il tuo progetto' : 'Experts are analyzing your project')}
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" />
                {language === 'it' ? 'Aggiornamento automatico...' : 'Auto-refreshing...'}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Tabs */}
      {(project.phase === 'completed' || experts.length > 0) && (
        <Tabs value={activeResultsTab} onValueChange={setActiveResultsTab} className="space-y-4">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="experts" data-testid="tab-experts">
              {isComparisonMode
                ? (language === 'it' ? 'Modelli AI' : 'AI Models')
                : t('experts')} ({experts.length})
            </TabsTrigger>
            <TabsTrigger value="synthesis" data-testid="tab-synthesis">
              {t('synthesis')}
            </TabsTrigger>
            <TabsTrigger value="chat" data-testid="tab-chat">
              {language === 'it' ? 'Chat Manager' : 'Manager Chat'}
            </TabsTrigger>
            <TabsTrigger value="versions" data-testid="tab-versions">
              {t('versions')}
            </TabsTrigger>
          </TabsList>

          {/* Experts Tab */}
          <TabsContent value="experts" className="space-y-4">
            {experts.length === 0 ? (
              <Card className="bg-card border-border border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <AlertCircle className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground text-center">
                    {language === 'it'
                      ? (isComparisonMode ? 'Nessun modello ancora eseguito.' : 'Nessun esperto ancora eseguito.')
                      : (isComparisonMode ? 'No models executed yet.' : 'No experts executed yet.')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Expert List */}
                <div className="space-y-2">
                  {/* Step-by-step mode controls */}
                  {project?.execution_mode === 'step_by_step' && project?.phase === 'paused' && (
                    <Card className="bg-info/10 border-info/30">
                      <CardContent className="p-3 space-y-3">
                        <div className="flex items-center gap-2 text-sm text-info">
                          <Clock className="w-4 h-4" />
                          <span>{language === 'it' ? 'Modalità Step-by-Step' : 'Step-by-Step Mode'}</span>
                        </div>

                        {/* Show different buttons based on state */}
                        {allExpertsCompleted ? (
                          // All experts done - show "Create final synthesis" button
                          <Button
                            size="sm"
                            onClick={handleGenerateFinalSynthesis}
                            disabled={executing}
                            className="w-full gap-2 bg-success hover:bg-success/90"
                          >
                            {executing ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Sparkles className="w-3 h-3" />
                            )}
                            {language === 'it' ? 'Crea la sintesi finale' : 'Create final synthesis'}
                          </Button>
                        ) : (
                          // Still have pending experts - show Continue and Continue & Finish buttons
                          <div className="flex flex-col gap-2">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={handleContinueExecution}
                                disabled={executing}
                                className="flex-1 gap-1"
                              >
                                {executing ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Play className="w-3 h-3" />
                                )}
                                {language === 'it' ? 'Continua' : 'Continue'}
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={handleContinueAndFinish}
                                disabled={executing}
                                className="flex-1 gap-1"
                                title={language === 'it'
                                  ? 'Continua automaticamente fino al completamento di tutti gli esperti'
                                  : 'Continue automatically until all experts are completed'}
                              >
                                {executing ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <ChevronRight className="w-3 h-3" />
                                )}
                                {language === 'it' ? 'Continua e finisci' : 'Continue & finish'}
                              </Button>
                            </div>
                            <p className="text-xs text-muted-foreground text-center">
                              {isComparisonMode
                                ? (language === 'it'
                                  ? `${experts.filter(e => e.status === 'completed').length}/${experts.length} modelli completati`
                                  : `${experts.filter(e => e.status === 'completed').length}/${experts.length} models completed`)
                                : (language === 'it'
                                  ? `${experts.filter(e => e.status === 'completed').length}/${experts.length} esperti completati`
                                  : `${experts.filter(e => e.status === 'completed').length}/${experts.length} experts completed`)}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {experts.map((expert, index) => {
                    const isNextPending = project?.execution_mode === 'step_by_step' &&
                      project?.phase === 'paused' &&
                      expert.status === 'pending' &&
                      !experts.slice(0, index).some(e => e.status === 'pending');

                    return (
                      <Card
                        key={expert.id}
                        className={`bg-card border-border cursor-pointer transition-all ${selectedExpertId === expert.id ? 'border-primary ring-1 ring-primary/30' : 'hover:border-muted-foreground/50'
                          } ${isNextPending ? 'border-success/50' : ''}`}
                        onClick={() => setSelectedExpertId(expert.id)}
                        data-testid={`expert-card-${expert.id}`}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm truncate">{expert.role}</span>
                            <div className="flex items-center gap-1">
                              {/* Settings button for expert */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0 hover:bg-primary/20"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openExpertSettings({
                                        role: expert.role,
                                        custom_prompt: expert.custom_prompt,
                                        default_prompt: expert.prompt || expert.default_prompt,
                                        mcp_servers: expert.mcp_servers || [],
                                        llm_model: expert.llm_model,
                                        response_length: expert.response_length || 'medium'
                                      }, index);
                                    }}
                                    title={language === 'it'
                                      ? (isComparisonMode ? 'Impostazioni modello' : 'Impostazioni esperto')
                                      : (isComparisonMode ? 'Model settings' : 'Expert settings')}
                                  >
                                    <Settings className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{language === 'it'
                                    ? (isComparisonMode ? 'Impostazioni (prompt, MCP)' : 'Impostazioni (prompt, MCP)')
                                    : (isComparisonMode ? 'Settings (prompt, MCP)' : 'Settings (prompt, MCP)')}</p>
                                </TooltipContent>
                              </Tooltip>
                              <StatusBadge status={expert.status} language={language} />
                              {/* Play button for step-by-step mode */}
                              {isNextPending && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 hover:bg-success/20"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleExecuteExpert(expert.id);
                                  }}
                                  disabled={executingExpertId === expert.id}
                                  title={language === 'it'
                                    ? (isComparisonMode ? 'Esegui questo modello' : 'Esegui questo esperto')
                                    : (isComparisonMode ? 'Execute this model' : 'Execute this expert')}
                                >
                                  {executingExpertId === expert.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin text-success" />
                                  ) : (
                                    <Play className="w-4 h-4 text-success" />
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span className="truncate">{expert.llm_model.split('/').pop()}</span>
                            <div className="flex items-center gap-2">
                              {expert.confidence_score && <ConfidenceBadge score={expert.confidence_score} />}
                              <span>{formatCost(expert.cost || 0)}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Expert Detail */}
                <div className="lg:col-span-2">
                  {selectedExpert ? (
                    <Card className="bg-card border-border h-full flex flex-col">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <User className="w-5 h-5 text-muted-foreground" />
                            <CardTitle className="font-heading">{selectedExpert.role}</CardTitle>
                            {selectedExpert.confidence_score && (
                              <Badge variant="outline">
                                {t('confidence')}: {selectedExpert.confidence_score}/10
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <StatusBadge status={selectedExpert.status} language={language} />
                            {selectedExpert.response && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCopyExpertResponse(selectedExpert.response)}
                                className="gap-1"
                                data-testid="copy-expert-btn"
                              >
                                <Copy className="w-3 h-3" />
                                {language === 'it' ? 'Copia' : 'Copy'}
                              </Button>
                            )}
                            {/* Show Regenerate button when model was changed from the one used to generate response */}
                            {selectedExpert.response && selectedExpert.executed_with_model &&
                              selectedExpert.llm_model !== selectedExpert.executed_with_model && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleRegenerateExpert(selectedExpert)}
                                      disabled={regeneratingExpert || selectedExpert.status === 'running'}
                                      className="gap-1 border-warning text-warning hover:bg-warning/10"
                                      data-testid="regenerate-expert-btn"
                                    >
                                      {regeneratingExpert || selectedExpert.status === 'running'
                                        ? <Loader2 className="w-3 h-3 animate-spin" />
                                        : <RefreshCw className="w-3 h-3" />
                                      }
                                      {language === 'it' ? 'Rigenera' : 'Regenerate'}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{language === 'it'
                                      ? `Rigenera la risposta con ${selectedExpert.llm_model} (era ${selectedExpert.executed_with_model})`
                                      : `Regenerate response with ${selectedExpert.llm_model} (was ${selectedExpert.executed_with_model})`}</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            {(project.phase === 'completed' || project.phase === 'recalculating' || project.phase === 'executing') && ['completed', 'waiting_for_input'].includes(selectedExpert.status) && (
                              /* Chiarimenti button removed as requested */
                              null
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="font-code">{selectedExpert.llm_model}</span>
                          <span className="flex items-center gap-1">
                            {language === 'it' ? '€' : '$'} {formatCost(selectedExpert.cost || 0).slice(1)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Hash className="w-3 h-3" />{selectedExpert.tokens}
                          </span>
                          {selectedExpert.duration_seconds > 0 && (
                            <span className="flex items-center gap-1">
                              <Timer className="w-3 h-3" />{selectedExpert.duration_seconds.toFixed(1)}s
                            </span>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="flex-1 min-h-0 flex flex-col gap-4">
                        {/* Model selector and chat header */}
                        <div className="flex items-center justify-between gap-3 pb-2 border-b border-border">
                          <div className="flex items-center gap-2">
                            <MessageCircle className="w-4 h-4 text-primary" />
                            <h4 className="text-sm font-medium">
                              {language === 'it'
                                ? (isComparisonMode ? 'Chat con il modello' : 'Chat con l\'esperto')
                                : (isComparisonMode ? 'Chat with model' : 'Chat with expert')}
                            </h4>
                            {(clarificationMessages.length > 0 || selectedExpert.response) && (
                              <Badge variant="secondary" className="text-xs">
                                {clarificationMessages.length + (selectedExpert.response ? 1 : 0)}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-muted-foreground whitespace-nowrap">
                              {language === 'it' ? 'Modello' : 'Model'}
                            </Label>
                            <div className="w-64">
                              <ModelSelector
                                value={selectedExpert.llm_model}
                                onChange={handleSelectedExpertModelChange}
                                models={models}
                                openSourceOnly={false}
                                language={language}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Chat messages - includes expert opinion as first message */}
                        <div className="h-[600px] overflow-y-auto space-y-2 bg-muted/30 rounded-lg p-3">
                          {!selectedExpert.response && clarificationMessages.length === 0 ? (
                            <div className="flex items-center justify-center h-full text-muted-foreground">
                              {selectedExpert.status === 'running' ? (
                                <div className="flex flex-col items-center gap-2">
                                  <Loader2 className="w-8 h-8 animate-spin" />
                                  <span>{language === 'it' ? 'In elaborazione...' : 'Processing...'}</span>
                                </div>
                              ) : selectedExpert.status === 'pending' ? (
                                <span>{language === 'it' ? 'In attesa...' : 'Waiting...'}</span>
                              ) : (
                                <p className="text-xs text-muted-foreground text-center">
                                  {language === 'it'
                                    ? 'Nessun messaggio. Scrivi per chiedere chiarimenti.'
                                    : 'No messages. Write to ask for clarifications.'}
                                </p>
                              )}
                            </div>
                          ) : (
                            <>
                              {/* Expert's opinion as first message */}
                              {selectedExpert.response && (
                                <div className="flex justify-start">
                                  <div className="max-w-[95%] rounded-lg px-4 py-3 bg-background border">
                                    <div className="text-xs text-muted-foreground mb-2 font-medium">
                                      {selectedExpert.role}
                                    </div>
                                    <div className="prose prose-invert prose-sm max-w-none markdown-content">
                                      <ReactMarkdown
                                        components={{
                                          code({ node, inline, className, children, ...props }) {
                                            const match = /language-(\w+)/.exec(className || '');
                                            return !inline && match ? (
                                              <SyntaxHighlighter style={oneDark} language={match[1]} PreTag="div" {...props}>
                                                {String(children).replace(/\n$/, '')}
                                              </SyntaxHighlighter>
                                            ) : (
                                              <code className={className} {...props}>{children}</code>
                                            );
                                          },
                                          p({ children }) {
                                            return <p className="mb-3">{children}</p>;
                                          }
                                        }}
                                      >
                                        {selectedExpert.response}
                                      </ReactMarkdown>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Clarification messages */}
                              {clarificationMessages.map((msg, idx) => (
                                <div
                                  key={msg.id || idx}
                                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                >
                                  <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${msg.role === 'user'
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-background border'
                                    }`}>
                                    <div className="prose prose-sm prose-invert max-w-none markdown-content">
                                      <ReactMarkdown
                                        components={{
                                          p({ children }) {
                                            return <p className="mb-2">{children}</p>;
                                          }
                                        }}
                                      >
                                        {msg.content}
                                      </ReactMarkdown>
                                    </div>
                                  </div>
                                </div>
                              ))}

                              {/* Loading indicator when sending clarification */}
                              {sendingClarification && (
                                <div className="flex justify-start">
                                  <div className="max-w-[85%] rounded-lg px-3 py-2 bg-background border flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                                    <span className="text-sm text-muted-foreground">
                                      {language === 'it'
                                        ? (isComparisonMode ? 'Il modello sta pensando...' : 'L\'esperto sta pensando...')
                                        : (isComparisonMode ? 'Model is thinking...' : 'Expert is thinking...')}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                          <div ref={clarificationEndRef} />
                        </div>

                        {/* Chat input */}
                        <div className="flex gap-2">
                          <Input
                            placeholder={language === 'it'
                              ? (isComparisonMode ? 'Chiedi chiarimenti a questo modello...' : 'Chiedi chiarimenti a questo esperto...')
                              : (isComparisonMode ? 'Ask this model for clarifications...' : 'Ask this expert for clarifications...')}
                            value={clarificationInput}
                            onChange={(e) => setClarificationInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                if (clarificationInput.trim()) {
                                  handleSendClarification();
                                }
                              }
                            }}
                            disabled={sendingClarification || !['completed', 'clarification', 'waiting_for_input'].includes(selectedExpert.status)}
                            className="flex-1 h-9"
                          />
                          <Button
                            size="sm"
                            onClick={handleSendClarification}
                            disabled={
                              !clarificationInput.trim() ||
                              sendingClarification ||
                              !['completed', 'clarification', 'waiting_for_input'].includes(selectedExpert.status)
                            }
                            className="h-9"
                          >
                            {sendingClarification ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        {!['completed', 'clarification', 'waiting_for_input'].includes(selectedExpert.status) && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {language === 'it'
                              ? (isComparisonMode ? '⏳ Il modello deve completare l\'analisi prima di poter chattare.' : '⏳ L\'esperto deve completare l\'analisi prima di poter chattare.')
                              : (isComparisonMode ? '⏳ Model must complete analysis before you can chat.' : '⏳ Expert must complete analysis before you can chat.')}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="bg-card border-border h-full flex items-center justify-center">
                      <p className="text-muted-foreground">
                        {language === 'it'
                          ? (isComparisonMode ? 'Seleziona un modello' : 'Seleziona un esperto')
                          : (isComparisonMode ? 'Select a model' : 'Select an expert')}
                      </p>
                    </Card>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Synthesis Tab */}
          <TabsContent value="synthesis">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">
                  {t('synthesis')} {project.version > 1 && <span className="text-primary">V{project.version}</span>}
                </CardTitle>
                {project.final_synthesis && (
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(project.final_synthesis);
                        toast.success(language === 'it' ? 'Copiato negli appunti' : 'Copied to clipboard');
                      }}
                      data-testid="copy-synthesis-btn"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      {t('copy')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Generate complete report with synthesis + all expert opinions
                        let report = `# ${project.name || (language === 'it' ? 'Report Progetto' : 'Project Report')}\n\n`;
                        report += `**${language === 'it' ? 'Data' : 'Date'}:** ${new Date().toLocaleDateString()}\n\n`;
                        report += `---\n\n`;
                        report += `## ${language === 'it' ? 'Sintesi Finale' : 'Final Synthesis'}\n\n`;
                        report += project.final_synthesis + '\n\n';
                        report += `---\n\n`;
                        report += `## ${isComparisonMode
                          ? (language === 'it' ? 'Risposte dei Modelli' : 'Model Responses')
                          : (language === 'it' ? 'Pareri degli Esperti' : 'Expert Opinions')}\n\n`;

                        experts.forEach((exp, i) => {
                          report += `### ${i + 1}. ${exp.role}`;
                          if (exp.confidence_score) {
                            report += ` (${language === 'it' ? 'Confidenza' : 'Confidence'}: ${exp.confidence_score}/10)`;
                          }
                          report += `\n\n`;
                          report += `**${language === 'it' ? 'Modello' : 'Model'}:** ${exp.llm_model}\n\n`;
                          if (exp.response) {
                            report += exp.response + '\n\n';
                          }
                          report += `---\n\n`;
                        });

                        // Download as markdown file
                        const blob = new Blob([report], { type: 'text/markdown' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${project.name || 'report'}-completo.md`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                        toast.success(language === 'it' ? 'Report completo scaricato' : 'Complete report downloaded');
                      }}
                      data-testid="download-report-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {language === 'it' ? 'Scarica Report' : 'Download Report'}
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {project.final_synthesis ? (
                  <ScrollArea className="h-[500px]">
                    <div className="prose prose-invert prose-sm max-w-none markdown-content">
                      <ReactMarkdown
                        components={{
                          code({ node, inline, className, children, ...props }) {
                            const match = /language-(\w+)/.exec(className || '');
                            return !inline && match ? (
                              <SyntaxHighlighter style={oneDark} language={match[1]} PreTag="div" {...props}>
                                {String(children).replace(/\n$/, '')}
                              </SyntaxHighlighter>
                            ) : (
                              <code className={className} {...props}>{children}</code>
                            );
                          }
                        }}
                      >
                        {project.final_synthesis}
                      </ReactMarkdown>
                    </div>
                  </ScrollArea>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    {language === 'it' ? 'Sintesi non ancora disponibile.' : 'Synthesis not yet available.'}
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Chat Tab */}
          <TabsContent value="chat">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    {language === 'it' ? 'Chat con il Manager' : 'Chat with Manager'}
                  </CardTitle>
                  <CardDescription>
                    {language === 'it'
                      ? 'Continua la conversazione con il Manager del Progetto. Ha accesso a tutti i pareri e alla sintesi.'
                      : 'Continue the conversation with the Project Manager. Has access to all opinions and synthesis.'}
                  </CardDescription>
                </div>
                {(chatHistory.length > 0 || project.final_synthesis || experts.length > 0) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Generate complete chronological report
                      let report = `# ${project.name || (language === 'it' ? 'Report Completo Progetto' : 'Complete Project Report')}\n\n`;
                      report += `**${language === 'it' ? 'Data creazione' : 'Created'}:** ${new Date(project.created_at).toLocaleDateString()}\n`;
                      report += `**${language === 'it' ? 'Data report' : 'Report date'}:** ${new Date().toLocaleDateString()}\n\n`;
                      report += `---\n\n`;

                      // 1. Original request (always first)
                      report += `## 📋 ${language === 'it' ? 'Richiesta Originale' : 'Original Request'}\n\n`;
                      report += project.prompt + '\n\n';

                      // 2. Build chronological timeline
                      const timeline = [];

                      // Add chat messages with timestamps
                      chatHistory.forEach((msg, idx) => {
                        timeline.push({
                          type: 'chat',
                          role: msg.role,
                          content: msg.content,
                          timestamp: msg.timestamp || project.created_at,
                        });
                      });

                      // Add experts with their created_at
                      experts.forEach((exp, idx) => {
                        if (exp.response) {
                          timeline.push({
                            type: 'expert',
                            expert: exp,
                            timestamp: exp.created_at || project.created_at,
                          });
                        }
                      });

                      // Add synthesis with its timestamp
                      if (project.final_synthesis) {
                        timeline.push({
                          type: 'synthesis',
                          content: project.final_synthesis,
                          timestamp: project.synthesis_timestamp || project.updated_at,
                        });
                      }

                      // Sort by timestamp
                      timeline.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

                      // 3. Separate pre-synthesis chat and post-synthesis chat
                      const synthesisTime = project.synthesis_timestamp ? new Date(project.synthesis_timestamp) : null;

                      const preSynthesisChat = timeline.filter(item =>
                        item.type === 'chat' && (!synthesisTime || new Date(item.timestamp) < synthesisTime)
                      );
                      const expertItems = timeline.filter(item => item.type === 'expert');
                      const synthesisItem = timeline.find(item => item.type === 'synthesis');
                      const postSynthesisChat = timeline.filter(item =>
                        item.type === 'chat' && synthesisTime && new Date(item.timestamp) >= synthesisTime
                      );

                      // 4. Write pre-synthesis conversation with Manager
                      if (preSynthesisChat.length > 0) {
                        report += `---\n\n`;
                        report += `## 💬 ${language === 'it' ? 'Conversazione Iniziale con il Manager' : 'Initial Conversation with Manager'}\n\n`;
                        preSynthesisChat.forEach((msg) => {
                          const role = msg.role === 'user'
                            ? (language === 'it' ? '👤 Utente' : '👤 User')
                            : (language === 'it' ? '🤖 Manager' : '🤖 Manager');
                          report += `### ${role}\n\n${msg.content}\n\n`;
                        });
                      }

                      // 5. Write expert opinions
                      if (expertItems.length > 0) {
                        report += `---\n\n`;
                        const entityName = isComparisonMode
                          ? (language === 'it' ? 'Modelli AI' : 'AI Models')
                          : (language === 'it' ? 'Esperti' : 'Experts');
                        report += `## 👥 ${language === 'it' ? `Pareri degli ${entityName}` : `${entityName} Opinions`}\n\n`;

                        expertItems.forEach((item, i) => {
                          const exp = item.expert;
                          report += `### ${i + 1}. ${exp.role}`;
                          if (exp.confidence_score) {
                            report += ` (${language === 'it' ? 'Confidenza' : 'Confidence'}: ${exp.confidence_score}/10)`;
                          }
                          report += `\n\n`;
                          report += `**${language === 'it' ? 'Modello' : 'Model'}:** \`${exp.llm_model}\`\n\n`;
                          report += exp.response + '\n\n';

                          // Include clarification chat if exists
                          if (exp.clarification_chat?.length > 0) {
                            report += `#### ${language === 'it' ? 'Chat con l\'esperto' : 'Expert Chat'}\n\n`;
                            exp.clarification_chat.forEach(chat => {
                              const chatRole = chat.role === 'user'
                                ? (language === 'it' ? '👤 Utente' : '👤 User')
                                : `🤖 ${exp.role}`;
                              report += `**${chatRole}:**\n\n${chat.content}\n\n`;
                            });
                          }
                        });
                      }

                      // 6. Write final synthesis
                      if (synthesisItem) {
                        report += `---\n\n`;
                        report += `## 📊 ${language === 'it' ? 'Sintesi Finale' : 'Final Synthesis'}\n\n`;
                        report += synthesisItem.content + '\n\n';
                      }

                      // 7. Write post-synthesis conversation
                      if (postSynthesisChat.length > 0) {
                        report += `---\n\n`;
                        report += `## 💬 ${language === 'it' ? 'Conversazione Successiva con il Manager' : 'Follow-up Conversation with Manager'}\n\n`;
                        postSynthesisChat.forEach((msg) => {
                          const role = msg.role === 'user'
                            ? (language === 'it' ? '👤 Utente' : '👤 User')
                            : (language === 'it' ? '🤖 Manager' : '🤖 Manager');
                          report += `### ${role}\n\n${msg.content}\n\n`;
                        });
                      }

                      // 8. Add metadata footer
                      report += `---\n\n`;
                      report += `## 📈 ${language === 'it' ? 'Statistiche' : 'Statistics'}\n\n`;
                      report += `- **${language === 'it' ? 'Costo totale' : 'Total cost'}:** ${formatCost(project.total_cost || 0)}\n`;
                      report += `- **${language === 'it' ? 'Token totali' : 'Total tokens'}:** ${(project.total_tokens || 0).toLocaleString()}\n`;
                      report += `- **${isComparisonMode
                        ? (language === 'it' ? 'Numero modelli' : 'Number of models')
                        : (language === 'it' ? 'Numero esperti' : 'Number of experts')}:** ${experts.length}\n`;

                      // Download
                      const blob = new Blob([report], { type: 'text/markdown' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${project.name || 'progetto'}-report-completo.md`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                      toast.success(language === 'it' ? 'Report completo scaricato' : 'Complete report downloaded');
                    }}
                    data-testid="download-chat-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {language === 'it' ? 'Scarica Report Completo' : 'Download Complete Report'}
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Chat messages - chronologically ordered */}
                  <div className="h-96 overflow-y-auto border border-border rounded-lg p-4 space-y-4 bg-background">
                    {(() => {
                      // Build chronological timeline of all messages
                      const timeline = [];

                      // Add chat history messages
                      chatHistory.forEach((msg, idx) => {
                        timeline.push({
                          type: 'chat',
                          role: msg.role,
                          content: msg.content,
                          timestamp: msg.timestamp || project.created_at,
                          key: `chat-${idx}`
                        });
                      });

                      // Add synthesis if available
                      if (project.phase === 'completed' && project.final_synthesis) {
                        timeline.push({
                          type: 'synthesis',
                          content: project.final_synthesis,
                          timestamp: project.synthesis_timestamp || project.updated_at,
                          key: 'synthesis'
                        });
                      }

                      // Sort by timestamp
                      timeline.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

                      if (timeline.length === 0) {
                        return (
                          <div className="flex items-center justify-center h-full text-muted-foreground">
                            <div className="text-center">
                              <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
                              <p>{language === 'it' ? 'Inizia una conversazione...' : 'Start a conversation...'}</p>
                            </div>
                          </div>
                        );
                      }

                      return timeline.map((item) => {
                        if (item.type === 'synthesis') {
                          return (
                            <div key={item.key} className="p-4 rounded-lg bg-gradient-to-r from-success/10 to-primary/10 border border-success/30">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2 text-success font-medium">
                                  <CheckCircle2 className="w-5 h-5" />
                                  {language === 'it' ? '📊 Sintesi Finale del Progetto' : '📊 Final Project Synthesis'}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    navigator.clipboard.writeText(project.final_synthesis);
                                    toast.success(language === 'it' ? 'Sintesi copiata' : 'Synthesis copied');
                                  }}
                                >
                                  <Copy className="w-3 h-3 mr-1" />
                                  {language === 'it' ? 'Copia' : 'Copy'}
                                </Button>
                              </div>
                              <div className="prose prose-invert prose-sm max-w-none text-sm markdown-content">
                                <ReactMarkdown>{item.content}</ReactMarkdown>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div key={item.key} className={`flex ${item.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%] p-3 rounded-lg ${item.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted'
                              }`}>
                              <div className="text-sm prose prose-invert prose-sm max-w-none markdown-content">
                                <ReactMarkdown>{item.content}</ReactMarkdown>
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                    {sendingChat && (
                      <div className="flex justify-start">
                        <div className="max-w-[80%] p-3 rounded-lg bg-muted flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm text-muted-foreground">
                            {language === 'it' ? 'Il Manager sta pensando...' : 'Manager is thinking...'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Chat input - always enabled */}
                  <div className="flex gap-2">
                    <Input
                      placeholder={language === 'it' ? 'Chiedi chiarimenti al Manager...' : 'Ask the Manager for clarifications...'}
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendChat()}
                      disabled={sendingChat}
                    />
                    <Button onClick={handleSendChat} disabled={sendingChat || !chatInput.trim()}>
                      {sendingChat ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Versions Tab */}
          <TabsContent value="versions">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <History className="w-5 h-5" />
                  {t('versions')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="p-4 rounded-lg border border-primary/30 bg-primary/5">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">{language === 'it' ? 'Versione' : 'Version'} {project.version}</span>
                        <span className="text-xs text-muted-foreground ml-2">({language === 'it' ? 'Attuale' : 'Current'})</span>
                      </div>
                      <span className="text-sm text-muted-foreground font-code">
                        {formatCost(project.total_cost || 0)}
                      </span>
                    </div>
                  </div>

                  {project.versions_history?.map((version, index) => (
                    <div key={index} className="p-4 rounded-lg border border-border">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium">{language === 'it' ? 'Versione' : 'Version'} {version.version}</span>
                          <span className="text-xs text-muted-foreground ml-2">
                            {new Date(version.timestamp).toLocaleString(language === 'it' ? 'it-IT' : 'en-US')}
                          </span>
                        </div>
                        <span className="text-sm text-muted-foreground font-code">
                          {formatCost(version.total_cost || 0)}
                        </span>
                      </div>
                    </div>
                  ))}

                  {(!project.versions_history || project.versions_history.length === 0) && project.version === 1 && (
                    <p className="text-muted-foreground text-center py-4">
                      {language === 'it' ? 'Nessuna versione precedente.' : 'No previous versions.'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Clarification Chat Dialog */}
      <Dialog open={clarificationChatOpen} onOpenChange={setClarificationChatOpen}>
        <DialogContent className="bg-card border-border sm:max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-primary" />
              {language === 'it'
                ? (['comparison', 'compare'].includes(project.analysis_mode) ? 'Chiarimenti Modello' : 'Chiarimenti Esperto')
                : (['comparison', 'compare'].includes(project.analysis_mode) ? 'Model Clarifications' : 'Expert Clarifications')}
            </DialogTitle>
            <DialogDescription>
              {clarificationExpert && (
                <span className="flex items-center gap-2 mt-1">
                  <User className="w-4 h-4" />
                  {clarificationExpert.role}
                  <Badge variant="outline" className="text-xs ml-2">
                    {clarificationExpert.llm_model?.split('/').pop()}
                  </Badge>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 flex flex-col min-h-0 pt-4">
            {/* Chat messages area */}
            <ScrollArea className="h-80 pr-4">
              <div className="space-y-4 pb-4">
                {clarificationMessages.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">
                      {language === 'it'
                        ? (isComparisonMode
                          ? 'Chiedi chiarimenti su questa risposta. Le risposte saranno condivise con tutti i modelli.'
                          : 'Chiedi chiarimenti su questa analisi. Le risposte saranno condivise con tutti gli esperti.')
                        : (isComparisonMode
                          ? 'Ask for clarifications on this response. Responses will be shared with all models.'
                          : 'Ask for clarifications on this analysis. Responses will be shared with all experts.')}
                    </p>
                  </div>
                ) : (
                  clarificationMessages.map((msg, index) => (
                    <div
                      key={msg.id || index}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-lg px-4 py-3 ${msg.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                          }`}
                      >
                        <div className="flex items-center gap-2 mb-1 text-xs opacity-70">
                          {msg.role === 'user' ? (
                            <>{language === 'it' ? 'Tu' : 'You'}</>
                          ) : (
                            <>{clarificationExpert?.role}</>
                          )}
                        </div>
                        <div className="text-sm prose prose-sm prose-invert max-w-none markdown-content">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={clarificationEndRef} />
              </div>
            </ScrollArea>

            {/* Input area */}
            <div className="border-t pt-4 mt-2">
              <div className="flex gap-2">
                <Textarea
                  placeholder={language === 'it'
                    ? 'Scrivi la tua richiesta di chiarimento...'
                    : 'Write your clarification request...'}
                  value={clarificationInput}
                  onChange={(e) => setClarificationInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendClarification();
                    }
                  }}
                  className="min-h-[60px] max-h-[120px] resize-none bg-background"
                  disabled={sendingClarification}
                />
                <Button
                  onClick={handleSendClarification}
                  disabled={!clarificationInput.trim() || sendingClarification}
                  className="self-end"
                >
                  {sendingClarification ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {language === 'it'
                  ? (isComparisonMode
                    ? '💡 I chiarimenti saranno condivisi nel contesto di tutti i modelli del progetto.'
                    : '💡 I chiarimenti saranno condivisi nel contesto di tutti gli esperti del progetto.')
                  : (isComparisonMode
                    ? '💡 Clarifications will be shared in the context of all project models.'
                    : '💡 Clarifications will be shared in the context of all project experts.')}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent className="bg-card border-border sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {language === 'it'
                ? (['comparison', 'compare'].includes(project.analysis_mode) ? 'Feedback Modello' : 'Feedback Esperto')
                : (['comparison', 'compare'].includes(project.analysis_mode) ? 'Model Feedback' : 'Expert Feedback')}
            </DialogTitle>
            <DialogDescription>
              {commentExpert && (
                <span className="flex items-center gap-2 mt-1">
                  <User className="w-4 h-4" />
                  {commentExpert.role}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label className="mb-2 block">
                {language === 'it' ? 'Il tuo feedback o correzione:' : 'Your feedback or correction:'}
              </Label>
              <Textarea
                placeholder={language === 'it'
                  ? (['comparison', 'compare'].includes(project.analysis_mode)
                    ? 'Scrivi cosa vorresti cambiare nella risposta di questo modello...'
                    : 'Scrivi cosa vorresti cambiare nell\'analisi di questo esperto...')
                  : (['comparison', 'compare'].includes(project.analysis_mode)
                    ? 'Write what you would like to change in this model\'s response...'
                    : 'Write what you would like to change in this expert\'s analysis...')}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                className="min-h-24 bg-background"
                data-testid="comment-input"
              />
            </div>

            <div className="p-3 rounded-lg bg-muted/50 space-y-3">
              <p className="text-xs text-muted-foreground">
                {language === 'it'
                  ? 'Scegli cosa rigenerare dopo aver salvato il commento:'
                  : 'Choose what to regenerate after saving the comment:'}
              </p>

              <div className="grid grid-cols-1 gap-2">
                {/* Save comment + regenerate this expert only */}
                <Button
                  onClick={() => {
                    setRecalculateMode('single');
                    handleComment();
                  }}
                  disabled={submittingComment || !commentText.trim()}
                  variant="outline"
                  className="w-full justify-start gap-2 h-auto py-3"
                  data-testid="regenerate-expert-btn"
                >
                  <RefreshCw className="w-4 h-4 text-primary" />
                  <div className="text-left">
                    <div className="font-medium">
                      {language === 'it'
                        ? (['comparison', 'compare'].includes(project.analysis_mode) ? 'Rigenera solo questo modello' : 'Rigenera solo questo esperto')
                        : (['comparison', 'compare'].includes(project.analysis_mode) ? 'Regenerate only this model' : 'Regenerate only this expert')}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {language === 'it'
                        ? (['comparison', 'compare'].includes(project.analysis_mode)
                          ? 'Il modello ricalcolerà la sua risposta con il tuo feedback'
                          : 'L\'esperto ricalcolerà la sua analisi con il tuo feedback')
                        : (['comparison', 'compare'].includes(project.analysis_mode)
                          ? 'The model will recalculate its response with your feedback'
                          : 'The expert will recalculate their analysis with your feedback')}
                    </div>
                  </div>
                </Button>

                {/* Save comment + regenerate expert + final synthesis */}
                <Button
                  onClick={() => {
                    setRecalculateMode('single_and_synthesis');
                    handleComment();
                  }}
                  disabled={submittingComment || !commentText.trim()}
                  variant="outline"
                  className="w-full justify-start gap-2 h-auto py-3"
                  data-testid="regenerate-expert-synthesis-btn"
                >
                  <div className="flex items-center gap-1">
                    <RefreshCw className="w-4 h-4 text-warning" />
                    <Sparkles className="w-4 h-4 text-warning" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium">
                      {language === 'it'
                        ? (['comparison', 'compare'].includes(project.analysis_mode) ? 'Rigenera modello + Sintesi Finale' : 'Rigenera esperto + Sintesi Finale')
                        : (['comparison', 'compare'].includes(project.analysis_mode) ? 'Regenerate model + Final Synthesis' : 'Regenerate expert + Final Synthesis')}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {language === 'it'
                        ? (['comparison', 'compare'].includes(project.analysis_mode)
                          ? 'Ricalcola questo modello e poi aggiorna la sintesi finale'
                          : 'Ricalcola questo esperto e poi aggiorna la sintesi finale')
                        : (['comparison', 'compare'].includes(project.analysis_mode)
                          ? 'Recalculate this model and then update the final synthesis'
                          : 'Recalculate this expert and then update the final synthesis')}
                    </div>
                  </div>
                </Button>

                {/* Save comment only (for batch operations) */}
                <Button
                  onClick={() => {
                    setRecalculateMode('comment_only');
                    handleComment();
                  }}
                  disabled={submittingComment || !commentText.trim()}
                  variant="ghost"
                  className="w-full justify-start gap-2 h-auto py-3 border border-dashed border-border"
                  data-testid="save-comment-only-btn"
                >
                  <MessageSquare className="w-4 h-4 text-muted-foreground" />
                  <div className="text-left">
                    <div className="font-medium text-muted-foreground">
                      {language === 'it' ? 'Salva solo il commento' : 'Save comment only'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {language === 'it'
                        ? (['comparison', 'compare'].includes(project.analysis_mode)
                          ? 'Per commentare più modelli e rigenerare dopo'
                          : 'Per commentare più esperti e rigenerare dopo')
                        : (['comparison', 'compare'].includes(project.analysis_mode)
                          ? 'To comment on multiple models and regenerate later'
                          : 'To comment on multiple experts and regenerate later')}
                    </div>
                  </div>
                </Button>
              </div>
            </div>

            {submittingComment && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                {language === 'it' ? 'Elaborazione in corso...' : 'Processing...'}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Expert Settings Dialog */}
      <Dialog open={expertSettingsOpen} onOpenChange={setExpertSettingsOpen}>
        <DialogContent className="bg-card border-border sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Settings className="w-5 h-5" />
              {language === 'it'
                ? (['comparison', 'compare'].includes(project.analysis_mode) ? 'Impostazioni Modello' : 'Impostazioni Esperto')
                : (['comparison', 'compare'].includes(project.analysis_mode) ? 'Model Settings' : 'Expert Settings')}
            </DialogTitle>
            <DialogDescription>
              {selectedExpertForSettings && (
                <span className="flex items-center gap-2 mt-1">
                  <User className="w-4 h-4" />
                  {selectedExpertForSettings.role}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 pt-4">
            {/* Response Length Section */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <FileText className="w-4 h-4" />
                {language === 'it' ? 'Lunghezza Risposta' : 'Response Length'}
              </Label>
              <p className="text-xs text-muted-foreground">
                {language === 'it'
                  ? (['comparison', 'compare'].includes(project.analysis_mode)
                    ? 'Controlla la lunghezza e il livello di dettaglio della risposta di questo modello.'
                    : 'Controlla la lunghezza e il livello di dettaglio della risposta di questo esperto.')
                  : (['comparison', 'compare'].includes(project.analysis_mode)
                    ? 'Control the length and detail level of this model\'s response.'
                    : 'Control the length and detail level of this expert\'s response.')}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'brief', label: language === 'it' ? 'Breve' : 'Brief', desc: '500-700', icon: '📝' },
                  { value: 'medium', label: language === 'it' ? 'Media' : 'Medium', desc: '1000-1500', icon: '📄' },
                  { value: 'complete', label: language === 'it' ? 'Completa' : 'Complete', desc: language === 'it' ? 'Illimitata' : 'Unlimited', icon: '📚' },
                ].map(opt => (
                  <Button
                    key={opt.value}
                    variant={expertResponseLength === opt.value ? 'default' : 'outline'}
                    onClick={() => setExpertResponseLength(opt.value)}
                    className="flex flex-col h-auto py-3"
                  >
                    <span className="text-lg mb-1">{opt.icon}</span>
                    <span className="font-medium">{opt.label}</span>
                    <span className="text-xs text-muted-foreground">{opt.desc} {language === 'it' ? 'parole' : 'words'}</span>
                  </Button>
                ))}
              </div>
            </div>

            {/* LLM Model Section */}
            <div className="space-y-3 pt-4 border-t border-border">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <Cpu className="w-4 h-4" />
                {language === 'it' ? 'Modello LLM' : 'LLM Model'}
              </Label>
              <p className="text-xs text-muted-foreground">
                {language === 'it'
                  ? (['comparison', 'compare'].includes(project.analysis_mode)
                    ? 'Il modello LLM che verrà utilizzato. Cerca tra tutti i modelli disponibili su OpenRouter.'
                    : 'Il modello LLM che verrà utilizzato per questo esperto. Cerca tra tutti i modelli disponibili su OpenRouter.')
                  : (['comparison', 'compare'].includes(project.analysis_mode)
                    ? 'The LLM model that will be used. Search among all available models on OpenRouter.'
                    : 'The LLM model that will be used for this expert. Search among all available models on OpenRouter.')}
              </p>
              <ModelSelector
                value={expertLlmModel}
                onChange={setExpertLlmModel}
                models={models}
                openSourceOnly={false}
                language={language}
              />
            </div>

            {/* Custom Prompt Section */}
            <div className="space-y-3 pt-4 border-t border-border">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <Pencil className="w-4 h-4" />
                  {language === 'it'
                    ? (['comparison', 'compare'].includes(project.analysis_mode) ? 'Prompt per questo Modello' : 'Prompt per questo Esperto')
                    : (['comparison', 'compare'].includes(project.analysis_mode) ? 'Prompt for this Model' : 'Prompt for this Expert')}
                </Label>
                {selectedExpertForSettings?.default_prompt && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resetExpertPromptToDefault}
                    className="gap-1"
                  >
                    <RefreshCw className="w-3 h-3" />
                    {language === 'it' ? 'Ripristina' : 'Reset'}
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {language === 'it'
                  ? (['comparison', 'compare'].includes(project.analysis_mode)
                    ? 'Questo è il prompt assegnato dal Manager LLM per questo modello. Puoi modificarlo se necessario.'
                    : 'Questo è il prompt assegnato dal Manager LLM per questo esperto. Puoi modificarlo se necessario.')
                  : (['comparison', 'compare'].includes(project.analysis_mode)
                    ? 'This is the prompt assigned by the Manager LLM for this model. You can modify it if needed.'
                    : 'This is the prompt assigned by the Manager LLM for this expert. You can modify it if needed.')}
              </p>
              <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-500">
                    {language === 'it'
                      ? (['comparison', 'compare'].includes(project.analysis_mode)
                        ? '💡 Il contesto completo (prompt originale, conversazione con il Manager, documenti allegati, chiarimenti) viene aggiunto automaticamente. Qui vedi solo il task specifico.'
                        : '💡 Il contesto completo (prompt originale, conversazione con il Manager, domande di chiarimento, documenti allegati, chiarimenti di altri esperti) viene aggiunto automaticamente quando l\'esperto viene eseguito. Qui vedi solo il task specifico per questo esperto.')
                      : (['comparison', 'compare'].includes(project.analysis_mode)
                        ? '💡 The full context (original prompt, Manager conversation, attached documents, clarifications) is automatically added. Here you only see the specific task.'
                        : '💡 The full context (original prompt, Manager conversation, clarification Q&A, attached documents, other experts\' clarifications) is automatically added when the expert runs. Here you only see the specific task for this expert.')}
                  </p>
                </div>
              </div>
              <Textarea
                placeholder={language === 'it'
                  ? 'Il prompt verrà generato dal Manager...'
                  : 'The prompt will be generated by the Manager...'}
                value={expertCustomPrompt}
                onChange={(e) => setExpertCustomPrompt(e.target.value)}
                className="min-h-40 bg-background font-mono text-sm"
                data-testid="expert-custom-prompt"
              />
            </div>

            {/* MCP Servers Section */}
            <div className="space-y-3 pt-4 border-t border-border">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <Server className="w-4 h-4" />
                  {language === 'it' ? 'MCP Servers' : 'MCP Servers'}
                </Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMcpForm(!showMcpForm)}
                  className="gap-1"
                >
                  <Plus className="w-3 h-3" />
                  {language === 'it' ? 'Aggiungi' : 'Add'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {language === 'it'
                  ? (['comparison', 'compare'].includes(project.analysis_mode)
                    ? 'Configura i server MCP (Model Context Protocol) per estendere le capacità di questo modello.'
                    : 'Configura i server MCP (Model Context Protocol) per estendere le capacità di questo esperto.')
                  : (['comparison', 'compare'].includes(project.analysis_mode)
                    ? 'Configure MCP (Model Context Protocol) servers to extend this model\'s capabilities.'
                    : 'Configure MCP (Model Context Protocol) servers to extend this expert\'s capabilities.')}
              </p>

              {/* Form to add new MCP server */}
              {showMcpForm && (
                <div className="p-4 rounded-lg border border-border bg-muted/50 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">{language === 'it' ? 'Nome' : 'Name'} *</Label>
                      <Input
                        value={newMcpServer.name}
                        onChange={(e) => setNewMcpServer({ ...newMcpServer, name: e.target.value })}
                        placeholder="My MCP Server"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">URL *</Label>
                      <Input
                        value={newMcpServer.url}
                        onChange={(e) => setNewMcpServer({ ...newMcpServer, url: e.target.value })}
                        placeholder="https://mcp.example.com"
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">API Key ({language === 'it' ? 'opzionale' : 'optional'})</Label>
                    <Input
                      type="password"
                      value={newMcpServer.api_key || ''}
                      onChange={(e) => setNewMcpServer({ ...newMcpServer, api_key: e.target.value })}
                      placeholder="sk-..."
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={addMcpServerToExpert}
                      className="gap-1"
                    >
                      <Check className="w-3 h-3" />
                      {language === 'it' ? 'Aggiungi Server' : 'Add Server'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowMcpForm(false);
                        setNewMcpServer({ name: '', url: '', api_key: '' });
                      }}
                    >
                      {language === 'it' ? 'Annulla' : 'Cancel'}
                    </Button>
                  </div>
                </div>
              )}

              {expertMcpServers.length === 0 ? (
                <div className="p-4 rounded-lg border border-dashed border-border text-center">
                  <Server className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {language === 'it' ? 'Nessun MCP server configurato' : 'No MCP servers configured'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {expertMcpServers.map((server, index) => (
                    <div key={index} className="p-3 rounded-lg border border-border bg-background space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{server.name || `Server ${index + 1}`}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-destructive"
                          onClick={() => removeMcpServerFromExpert(index)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">{language === 'it' ? 'Nome' : 'Name'}</Label>
                          <Input
                            value={server.name}
                            onChange={(e) => {
                              const newServers = [...expertMcpServers];
                              newServers[index] = { ...newServers[index], name: e.target.value };
                              setExpertMcpServers(newServers);
                            }}
                            placeholder="My MCP Server"
                            className="h-8 text-sm bg-card"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">URL</Label>
                          <Input
                            value={server.url}
                            onChange={(e) => {
                              const newServers = [...expertMcpServers];
                              newServers[index] = { ...newServers[index], url: e.target.value };
                              setExpertMcpServers(newServers);
                            }}
                            placeholder="https://mcp.example.com"
                            className="h-8 text-sm bg-card"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">API Key ({language === 'it' ? 'opzionale' : 'optional'})</Label>
                          <Input
                            type="password"
                            value={server.api_key || ''}
                            onChange={(e) => {
                              const newServers = [...expertMcpServers];
                              newServers[index] = { ...newServers[index], api_key: e.target.value };
                              setExpertMcpServers(newServers);
                            }}
                            placeholder="sk-..."
                            className="h-8 text-sm bg-card"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{language === 'it' ? 'Descrizione' : 'Description'}</Label>
                          <Input
                            value={server.description || ''}
                            onChange={(e) => {
                              const newServers = [...expertMcpServers];
                              newServers[index] = { ...newServers[index], description: e.target.value };
                              setExpertMcpServers(newServers);
                            }}
                            placeholder={language === 'it' ? 'Descrizione server...' : 'Server description...'}
                            className="h-8 text-sm bg-card"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Save Button */}
            <div className="flex justify-end gap-2 pt-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => setExpertSettingsOpen(false)}
              >
                {language === 'it' ? 'Annulla' : 'Cancel'}
              </Button>
              <Button
                onClick={saveExpertSettings}
                disabled={savingExpertSettings}
                className="gap-2"
              >
                {savingExpertSettings ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                {language === 'it' ? 'Salva Impostazioni' : 'Save Settings'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Regenerate Final Synthesis Button - shown when project is completed */}
      {project.phase === 'completed' && (
        <div className="fixed bottom-6 right-6">
          <Button
            onClick={async () => {
              try {
                await projectApi.addComment(id, {
                  expert_id: 'orchestrator',
                  comment: 'User requested synthesis regeneration',
                  recalculate_mode: 'orchestrator'
                });
                toast.success(language === 'it' ? 'Rigenerazione sintesi avviata...' : 'Synthesis regeneration started...');
                await loadProject();
              } catch (error) {
                toast.error(language === 'it' ? 'Errore nella rigenerazione' : 'Regeneration failed');
              }
            }}
            className="gap-2 shadow-lg"
            variant="secondary"
            data-testid="regenerate-synthesis-btn"
          >
            <Sparkles className="w-4 h-4" />
            {language === 'it' ? 'Rigenera Sintesi Finale' : 'Regenerate Final Synthesis'}
          </Button>
        </div>
      )}

      {/* Synthesis Generation Modal */}
      <Dialog open={synthesisModalOpen} onOpenChange={() => { }}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              {language === 'it' ? 'Generazione Sintesi in Corso' : 'Generating Synthesis'}
            </DialogTitle>
            <DialogDescription>
              {language === 'it'
                ? 'Il sistema sta elaborando la sintesi finale basata sulle opinioni degli esperti. Questa operazione potrebbe richiedere alcuni minuti.'
                : 'The system is processing the final synthesis based on expert opinions. This operation may take a few minutes.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-6 space-y-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full border-4 border-muted animate-pulse"></div>
              <Sparkles className="w-8 h-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              {language === 'it'
                ? 'Attendere prego...'
                : 'Please wait...'}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="destructive"
              onClick={handleInterruptSynthesis}
              className="w-full gap-2"
            >
              <XCircle className="w-4 h-4" />
              {language === 'it' ? 'Interrompi Generazione' : 'Interrupt Generation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
